import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Archive, ArrowUp, BarChart3, BookOpen, CheckCircle, ExternalLink, LayoutDashboard, ListChecks, LogOut, MessageSquare, Package, Plus, RefreshCw, Search, Settings, ShieldCheck, Trash2, X } from 'lucide-react';
import {
  applyFeedbackCorrectionAPI,
  checkBackendHealthAPI,
  clearE2eResultsAPI,
  deleteE2eResultAPI,
  deleteVerifiedProductAPI,
  getAnalyticsSummaryAPI,
  getCostControlsAPI,
  getE2eResultAPI,
  getE2eResultsAPI,
  getReviewQueueAPI,
  listOpsEventsAPI,
  listFeedbackInboxAPI,
  listVerifiedCatalogAPI,
  restoreVerifiedProductAPI,
  saveVerifiedProductAPI,
  sendBackendSentryTestAPI,
  updateFeedbackStatusAPI,
  updateOpsEventStatusAPI,
  updateReviewQueueStatusAPI,
  updateReviewQueueCategoryAPI,
} from '../utils/api';
import { captureFrontendMessage } from '../sentry';
import { LearningArchivePanel } from './LearningArchivePanel';

const blankForm = {
  name: '',
  brand: '',
  aliases: '',
  ingredients: '',
  status: 'vegan',
  sourceUrl: '',
  sourceType: 'manufacturer',
  verificationReason: '',
  verifiedBy: '',
  notes: '',
  expiresAt: '',
};

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'needs_review', label: 'Needs review' },
  { id: 'user_flag', label: 'User flagged' },
  { id: 'source_conflict', label: 'Source conflicts' },
];

const FLAG_CATEGORIES = [
  { id: 'vegan_status', label: 'Vegan status' },
  { id: 'product_match', label: 'Product match' },
  { id: 'ingredients', label: 'Ingredients' },
  { id: 'flavor_variant', label: 'Flavor or variant' },
  { id: 'allergen', label: 'Allergen' },
  { id: 'alternatives', label: 'Alternatives' },
  { id: 'source_trust', label: 'Source or trust' },
  { id: 'technical_problem', label: 'Technical problem' },
  { id: 'other', label: 'Other' },
];

const flagCategoryLabel = (value) =>
  FLAG_CATEGORIES.find((category) => category.id === value)?.label || 'Not categorized';

const FAILURE_REASONS = new Set([
  'exact_source_missing',
  'missing_ingredients',
  'barcode_missing_ingredients',
  'barcode_not_found',
  'backend_error',
  'lookup_failed',
]);

const FAILURE_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'source', label: 'Source' },
  { id: 'barcode', label: 'Barcode' },
  { id: 'backend', label: 'Backend' },
];

const FEEDBACK_FILTERS = [
  { id: 'open', label: 'Open' },
  { id: 'reviewing', label: 'Reviewing' },
  { id: 'resolved', label: 'Resolved' },
  { id: 'dismissed', label: 'Dismissed' },
  { id: 'all', label: 'All' },
];

const QUEUE_STATUS_FILTERS = [
  { id: 'open', label: 'Open' },
  { id: 'reviewing', label: 'Investigating' },
  { id: 'resolved', label: 'Resolved' },
  { id: 'dismissed', label: 'Cleared' },
  { id: 'all', label: 'All records' },
];

const VERIFIED_REVIEW_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'needs_review', label: 'Needs re-review' },
  { id: 'expiring_soon', label: 'Expiring soon' },
  { id: 'current', label: 'Current' },
];

const DOC_LINKS = [
  {
    group: 'Start Here',
    links: [
      { label: 'Vision Board', detail: 'Personal project dashboard', href: '/project-dashboard.html' },
      { label: 'Foundation Overview', detail: 'Repo, workflow, and project source of truth', href: '/docs/foundation-overview.html' },
      { label: 'Feature Map', detail: 'Audit index and feature fat files', href: '/docs/feature-map.html' },
      { label: 'Launch Readiness', detail: 'Hybrid public launch plan', href: '/docs/launch-readiness.html' },
      { label: 'Hybrid Launch Plan', detail: 'Web beta first, native iOS scanner next', href: '/docs/hybrid-launch-plan.html' },
      { label: 'Public Web Beta', detail: 'Hosting, auth, and persistence plan', href: '/docs/public-web-beta-plan.html' },
    ],
  },
  {
    group: 'Operations',
    links: [
      { label: 'Admin Review', detail: 'Command, verified products, errors, queue', href: '/docs/features/admin-review.md' },
      { label: 'How To: Analytics', detail: 'Read metrics and recent activity', href: '/docs/how-to-analytics.html' },
      { label: 'Product Lookup', detail: 'Search flow, source matching, variants', href: '/docs/features/product-lookup.md' },
      { label: 'Scanner', detail: 'Barcode/image scan audit and native iOS notes', href: '/docs/features/scanner.md' },
      { label: 'Deployment', detail: 'Mac mini, launchd, Tailscale', href: '/docs/features/deployment.md' },
      { label: 'Security', detail: 'Keys, secrets, and safe handling', href: '/docs/features/security.md' },
    ],
  },
  {
    group: 'Policies',
    links: [
      { label: 'Privacy and Data', detail: 'User-facing privacy foundation', href: '/docs/privacy-user-data.html' },
      { label: 'Retention Archive', detail: 'Short-term evidence, long-term learning', href: '/docs/retention-learning-archive.html' },
      { label: 'Costs and Services', detail: 'Spend and API watchlist', href: '/docs/costs-and-services.html' },
      { label: 'To Do', detail: 'Synced planning tasks', href: '/docs/todo.html' },
    ],
  },
];

function splitLines(value) {
  return String(value || '')
    .split(/\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function formatDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  }).format(date);
}

function formatDateTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function formatRefreshTime() {
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date());
}

function formatOverviewDate(value = new Date()) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

function formatOverviewClock(value = new Date()) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

const DAILY_QUOTES = [
  { text: 'Just keep swimming.', source: 'Finding Nemo', kind: 'Movie' },
  { text: 'With great power comes great responsibility.', source: 'Spider-Man', kind: 'Movie' },
  { text: 'There is no try. Do, or do not.', source: 'Star Wars: The Empire Strikes Back', kind: 'Movie' },
  { text: 'Why do we fall? So we can learn to pick ourselves up.', source: 'Batman Begins', kind: 'Movie' },
  { text: 'Get busy living, or get busy dying.', source: 'The Shawshank Redemption', kind: 'Movie' },
  { text: 'Carpe diem. Seize the day.', source: 'Dead Poets Society', kind: 'Movie' },
  { text: 'It\'s not who I am underneath, but what I do that defines me.', source: 'Batman Begins', kind: 'Movie' },
  { text: 'Great men are not born great, they grow great.', source: 'The Godfather Part II', kind: 'Movie' },
  { text: 'All we have to decide is what to do with the time that is given to us.', source: 'The Lord of the Rings', kind: 'Movie' },
  { text: 'The flower that blooms in adversity is the most rare and beautiful of all.', source: 'Mulan', kind: 'Movie' },
  { text: 'Oh yes, the past can hurt. But the way I see it, you can either run from it or learn from it.', source: 'The Lion King', kind: 'Movie' },
  { text: 'Life moves pretty fast. If you don\'t stop and look around once in a while, you could miss it.', source: 'Ferris Bueller\'s Day Off', kind: 'Movie' },
  { text: 'To infinity and beyond!', source: 'Toy Story', kind: 'Movie' },
  { text: 'You must be shapeless, formless, like water.', source: 'Bruce Lee', kind: 'Sports / history' },
  { text: 'I\'ve failed over and over and over again in my life. And that is why I succeed.', source: 'Michael Jordan', kind: 'Sports' },
  { text: 'Hard work beats talent when talent doesn\'t work hard.', source: 'Tim Notke', kind: 'Sports' },
  { text: 'You miss 100% of the shots you don\'t take.', source: 'Wayne Gretzky', kind: 'Sports' },
  { text: 'It\'s hard to beat a person who never gives up.', source: 'Babe Ruth', kind: 'Sports' },
  { text: 'Success is no accident. It is hard work, perseverance, learning, studying, sacrifice.', source: 'Pelé', kind: 'Sports' },
  { text: 'Champions keep playing until they get it right.', source: 'Billie Jean King', kind: 'Sports' },
  { text: 'Excellence is not a singular act, but a habit.', source: 'Shaquille O\'Neal', kind: 'Sports' },
  { text: 'Don\'t count the days, make the days count.', source: 'Muhammad Ali', kind: 'Sports' },
  { text: 'The only way to prove that you\'re a good sport is to lose.', source: 'Ernie Banks', kind: 'Sports' },
  { text: 'Float like a butterfly, sting like a bee.', source: 'Muhammad Ali', kind: 'Sports' },
  { text: 'Be the change that you wish to see in the world.', source: 'Mahatma Gandhi', kind: 'History' },
  { text: 'The only thing we have to fear is fear itself.', source: 'Franklin D. Roosevelt', kind: 'History' },
  { text: 'Well done is better than well said.', source: 'Benjamin Franklin', kind: 'History' },
  { text: 'If you want to lift yourself up, lift up someone else.', source: 'Booker T. Washington', kind: 'History' },
  { text: 'I have not failed. I\'ve just found 10,000 ways that won\'t work.', source: 'Thomas Edison', kind: 'History' },
  { text: 'Imagination is more important than knowledge.', source: 'Albert Einstein', kind: 'History' },
  { text: 'It always seems impossible until it\'s done.', source: 'Nelson Mandela', kind: 'History' },
  { text: 'The time is always right to do what is right.', source: 'Martin Luther King Jr.', kind: 'History' },
  { text: 'Darkness cannot drive out darkness; only light can do that.', source: 'Martin Luther King Jr.', kind: 'History' },
  { text: 'Do what you can, with what you have, where you are.', source: 'Theodore Roosevelt', kind: 'History' },
  { text: 'We are what we repeatedly do. Excellence, then, is not an act, but a habit.', source: 'Aristotle (as popularized)', kind: 'History' },
  { text: 'Fortune favors the bold.', source: 'Latin proverb / Virgil', kind: 'History' },
  { text: 'What you do makes a difference, and you have to decide what kind of difference you want to make.', source: 'Jane Goodall', kind: 'History' },
  { text: 'Start where you are. Use what you have. Do what you can.', source: 'Arthur Ashe', kind: 'Sports / history' },
  { text: 'Never let the fear of striking out keep you from playing the game.', source: 'Babe Ruth', kind: 'Sports' },
  { text: 'The man who moves a mountain begins by carrying away small stones.', source: 'Confucius', kind: 'History' },
  { text: 'Keep your eyes on the stars, and your feet on the ground.', source: 'Theodore Roosevelt', kind: 'History' },
  { text: 'Every strike brings me closer to the next home run.', source: 'Babe Ruth', kind: 'Sports' },
  { text: 'Yesterday is history, tomorrow is a mystery, but today is a gift.', source: 'Kung Fu Panda', kind: 'Movie' },
  { text: 'Happiness can be found even in the darkest of times, if one only remembers to turn on the light.', source: 'Harry Potter and the Prisoner of Azkaban', kind: 'Movie' },
  { text: 'You control your destiny — you don\'t need magic to do it.', source: 'Merida, Brave', kind: 'Movie' },
  { text: 'Sometimes the right path is not the easiest one.', source: 'Pocahontas', kind: 'Movie' },
  { text: 'Our lives are defined by opportunities, even the ones we miss.', source: 'The Curious Case of Benjamin Button', kind: 'Movie' },
  { text: 'Strive for progress, not perfection.', source: 'Unknown / pop culture mantra', kind: 'Sports' },
  { text: 'An investment in knowledge pays the best interest.', source: 'Benjamin Franklin', kind: 'History' },
  { text: 'Courage is resistance to fear, mastery of fear — not absence of fear.', source: 'Mark Twain', kind: 'History' },
  { text: 'The secret of getting ahead is getting started.', source: 'Mark Twain (as popularized)', kind: 'History' },
  { text: 'I am not afraid of storms, for I am learning how to sail my ship.', source: 'Louisa May Alcott', kind: 'History' },
  { text: 'We may encounter many defeats, but we must not be defeated.', source: 'Maya Angelou', kind: 'History' },
  { text: 'Act as if what you do makes a difference. It does.', source: 'William James', kind: 'History' },
  { text: 'Victory belongs to the most persevering.', source: 'Napoleon Bonaparte', kind: 'History' },
  { text: 'The will to win, the desire to succeed, the urge to reach your full potential… these are the keys.', source: 'Confucius (as popularized)', kind: 'History' },
  { text: 'Leave everything on the court.', source: 'Sports mantra', kind: 'Sports' },
  { text: 'Eye of the tiger.', source: 'Rocky III / Survivor', kind: 'Movie / sports' },
  { text: 'Go the distance.', source: 'Rocky', kind: 'Movie / sports' },
  { text: 'This is our chance to make history.', source: 'Hidden Figures', kind: 'Movie' },
];

function quoteOfTheDay(value = new Date()) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime()) || DAILY_QUOTES.length === 0) {
    return DAILY_QUOTES[0];
  }
  const local = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dayNumber = Math.floor(local.getTime() / 86400000);
  return DAILY_QUOTES[((dayNumber % DAILY_QUOTES.length) + DAILY_QUOTES.length) % DAILY_QUOTES.length];
}

function formatEventTime(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function formatDuration(value) {
  const ms = Number(value || 0);
  if (!Number.isFinite(ms) || ms <= 0) return '0s';
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return rest ? `${minutes}m ${rest}s` : `${minutes}m`;
}

function stripAnsi(value) {
  return String(value || '').replace(new RegExp(`${String.fromCharCode(27)}\\[[0-9;?]*[ -/]*[@-~]`, 'g'), '');
}

/** Keep Technical detail only for engineer-facing dumps, not plain golden summary repeats. */
function isEngineerTechnicalDetail(detail) {
  const text = stripAnsi(detail || '').trim();
  if (!text) return false;
  return (
    /Error:/i.test(text)
    || /TypeError|ReferenceError|AssertionError|TimeoutError/i.test(text)
    || /\bat\s+\S+/.test(text)
    || /browserType\.launch|Target (?:page|context|browser) has been closed|page\.goto|locator\(|strict mode violation|waiting for/i.test(text)
    || /ECONNREFUSED|ENOTFOUND|fetch failed|net::ERR_/i.test(text)
    || /HTTP\s*[45]\d\d/.test(text)
    || /\.(mjs|js|ts|tsx):\d+/.test(text)
    || /chromium|playwright/i.test(text)
    || /Traceback|Exception/i.test(text)
  );
}

function e2eStatusClass(status) {
  return status === 'pass'
    ? 'border-teal-600/25 bg-teal-600/10 text-teal-800'
    : 'border-red-400/25 bg-red-500/15 text-red-700';
}

function e2eImpactClass(impact) {
  if (impact === 'likely app issue') return 'border-amber-400/25 bg-amber-400/15 text-amber-900';
  if (impact === 'mixed') return 'border-sky-400/25 bg-sky-400/15 text-sky-900';
  return 'border-teal-600/25 bg-teal-600/10 text-teal-900';
}

function e2eImpactLabel(impact) {
  if (impact === 'likely app issue') return 'Probably the live app';
  if (impact === 'mixed') return 'Mixed - review both';
  return 'Probably the automated checker';
}

function e2eCheckStatusClass(status) {
  if (status === 'passed') return 'bg-teal-600/10 text-teal-800';
  if (status === 'failed') return 'bg-red-400/15 text-red-700';
  return 'bg-sky-400/15 text-sky-800';
}

function e2eSkipKind(check) {
  if (check?.status !== 'skipped') return '';
  if (check?.skipKind === 'intentional') return 'intentional';
  if (check?.skipKind === 'did-not-run') return 'did-not-run';
  const text = `${check?.reason || ''} ${check?.why || ''} ${check?.happened || ''}`.toLowerCase();
  if (/photo ocr|vision credits|off by default|save cost|skipped on purpose|intentionally/.test(text)) return 'intentional';
  return 'did-not-run';
}

function e2eSkipBreakdown(run) {
  const checks = Array.isArray(run?.checks) ? run.checks : [];
  const fromChecks = checks.reduce((acc, check) => {
    if (check.status !== 'skipped') return acc;
    if (e2eSkipKind(check) === 'intentional') acc.intentional += 1;
    else acc.didNotRun += 1;
    return acc;
  }, { intentional: 0, didNotRun: 0 });
  const skipped = Number(run?.summary?.skipped || 0);
  if (fromChecks.intentional + fromChecks.didNotRun > 0) return fromChecks;
  return {
    intentional: Number(run?.summary?.intentionalSkipped || 0),
    didNotRun: Number(run?.summary?.didNotRun || Math.max(0, skipped - Number(run?.summary?.intentionalSkipped || 0))),
  };
}

function e2eCheckStatusLabel(check) {
  if (check?.status === 'passed') return 'Worked';
  if (check?.status === 'failed') return 'Needs a look';
  return e2eSkipKind(check) === 'intentional' ? 'Skipped on purpose' : "Didn't run";
}

function e2eSuiteDisplayName(name) {
  if (name === 'Playwright live user flows') return 'Using the app like a visitor';
  if (name === 'Backend golden products live regression') return 'Known product benchmarks';
  if (name === 'live.spec.js') return 'Using the app like a visitor';
  return name || 'Live checks';
}

function e2eVerdictHeadline(run) {
  const failed = Number(run?.summary?.failed || 0);
  if (run?.status === 'pass') return 'Staging looks good - no action needed from this run.';
  return `Staging needs a look - ${failed} check${failed === 1 ? '' : 's'} did not match expectations.`;
}

function e2eCheckLabel(check) {
  return stripAnsi(check?.plainTitle || check?.title || 'Unnamed check');
}

function e2eCheckSummary(check) {
  return stripAnsi(check?.plainSummary || check?.happened || check?.reason || 'No plain-English detail was saved for this check.');
}

function e2eCheckWhy(check) {
  if (check?.why) return stripAnsi(check.why);
  if (check?.status === 'failed') return stripAnsi(check?.plainMessage || check?.happened || check?.reason || 'This check did not match expectations.');
  if (check?.status === 'skipped') {
    return e2eSkipKind(check) === 'intentional'
      ? stripAnsi(check?.reason || check?.happened || 'This optional check was skipped on purpose for this run.')
      : stripAnsi(check?.reason || check?.happened || "This check didn't run because an earlier issue stopped the suite.");
  }
  return 'This check passed.';
}

function e2eDetailTitle(view, suiteName) {
  if (view === 'all') return 'Things We Tested';
  if (view === 'passed') return 'Worked As Expected';
  if (view === 'failed') return 'Needs A Look';
  if (view === 'skipped') return "Skipped / Didn't Run";
  if (view === 'suite') return e2eSuiteDisplayName(suiteName);
  return 'Needs A Look';
}

function e2eCheckRows(run, view, suiteName) {
  const checks = Array.isArray(run?.checks) ? run.checks : [];
  if (checks.length === 0) return [];
  if (view === 'all') return checks;
  if (view === 'passed') return checks.filter((check) => check.status === 'passed');
  if (view === 'failed') return checks.filter((check) => check.status === 'failed');
  if (view === 'skipped') return checks.filter((check) => check.status === 'skipped');
  if (view === 'suite') {
    return checks.filter((check) =>
      check.suiteGroup === suiteName ||
      check.suite === suiteName ||
      (suiteName === 'Playwright live user flows' && check.suite === 'live.spec.js') ||
      (suiteName === 'Backend golden products live regression' && check.suite === 'Backend golden products live regression') ||
      e2eSuiteDisplayName(check.suiteGroup) === suiteName ||
      e2eSuiteDisplayName(check.suite) === suiteName
    );
  }
  return checks.filter((check) => check.status === 'failed');
}

function defaultE2eDetailViewForRun(run) {
  return Number(run?.summary?.failed || 0) > 0 ? 'failed' : 'all';
}

function verifiedReviewBadge(row) {
  if (row?.reviewStatus === 'needs_review' || row?.recordExpired) {
    return { label: 'Needs re-review', className: 'bg-amber-100 text-amber-950' };
  }
  if (row?.reviewStatus === 'expiring_soon') {
    return { label: 'Expiring soon', className: 'bg-yellow-100 text-yellow-950' };
  }
  return { label: 'Current', className: 'bg-emerald-100 text-emerald-950' };
}

function waitForRefreshCue(startedAt) {
  const remaining = 500 - (Date.now() - startedAt);
  if (remaining <= 0) return Promise.resolve();
  return new Promise((resolve) => window.setTimeout(resolve, remaining));
}

/** Accepts explicit N/A (spacing/case-insensitive) or a short substantive reason (letters, not only symbols/digits). */
function isValidVerificationReason(raw) {
  const s = String(raw || '').trim();
  if (!s) return false;
  const compact = s.replace(/\s+/g, '').toLowerCase();
  if (compact === 'n/a') return true;
  if (s.length < 3) return false;
  if (!/[a-zA-Z]/.test(s)) return false;
  return true;
}

function buildNotesPayload(verificationReason, notes) {
  const reasonLine = `Verification reason: ${String(verificationReason).trim()}`;
  const rest = String(notes || '').trim();
  return rest ? `${reasonLine}\n\n${rest}` : reasonLine;
}

function parseNotesPayload(notes) {
  const raw = String(notes || '').trim();
  const match = raw.match(/^Verification reason:\s*(.*?)(?:\n\n([\s\S]*))?$/i);
  if (!match) {
    return { verificationReason: raw ? 'N/A' : '', notes: raw };
  }
  return {
    verificationReason: String(match[1] || '').trim() || 'N/A',
    notes: String(match[2] || '').trim(),
  };
}

function prefillFromItem(item) {
  const result = item?.lastResult || {};
  const sources = Array.isArray(result.sources) ? result.sources : [];
  const firstSource = sources[0] || {};
  const ingredients = Array.isArray(result.ingredients) ? result.ingredients : [];
  const productName = adminDisplayText(item?.productMatch || result.productMatch || item?.query);

  return {
    ...blankForm,
    name: productName,
    aliases: item?.query && item.query !== productName ? item.query : '',
    ingredients: ingredients.join('\n'),
    status: result.status === 'not-vegan' ? 'not-vegan' : 'vegan',
    sourceUrl: firstSource.url || '',
    sourceType: firstSource.type || 'manufacturer',
    notes: `Reviewed from queue item #${item?.id || ''}. Previous result: ${result.status || 'unknown'}.`,
  };
}

function conciseJson(value) {
  if (!value) return '';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function adminDisplayText(value, fallback = '') {
  if (value == null) return fallback;
  if (typeof value === 'string' || typeof value === 'number') {
    const clean = String(value).trim();
    return clean && clean.toLowerCase() !== '[object object]' ? clean : fallback;
  }
  if (Array.isArray(value)) return value.map((item) => adminDisplayText(item)).filter(Boolean).join(' ') || fallback;
  if (typeof value === 'object') {
    return adminDisplayText(
      value.productMatch || value.productName || value.name || value.label || value.title || value.query || value.input || value.barcode,
      fallback,
    );
  }
  return String(value).trim() || fallback;
}

function reviewItemTitle(item, fallback = 'Review item') {
  return (
    adminDisplayText(item?.productMatch) ||
    adminDisplayText(item?.lastResult?.productMatch) ||
    adminDisplayText(item?.query) ||
    fallback
  );
}

function userFlagSummary(item) {
  const flag = item?.lastResult?.userFlag;
  if (!flag || typeof flag !== 'object') return '';
  return [adminDisplayText(flag.label), adminDisplayText(flag.detail)].filter(Boolean).join(': ');
}

function failureSummary(item) {
  const result = item?.lastResult || {};
  if (!result.failure && !result.error && !FAILURE_REASONS.has(item?.reason)) return null;
  const evidence = result.sourceEvidence || {};
  return {
    error: result.error || item.reason || 'LOOKUP_FAILED',
    message: result.message || 'No message saved.',
    resolvedVia: result.resolvedVia || 'unknown',
    status: result.status || 'unknown',
    productMatch: adminDisplayText(item?.productMatch || result.productMatch),
    query: adminDisplayText(item?.query),
    reason: item?.reason || '',
    createdAt: item?.createdAt || '',
    updatedAt: item?.updatedAt || '',
    sources: Array.isArray(result.sources) ? result.sources : [],
    sourceTypes: Array.isArray(evidence.sourceTypes) ? evidence.sourceTypes : [],
    trustedSourceCount: evidence.trustedSourceCount,
    bestSourceType: evidence.bestSourceType,
    bestTrustScore: evidence.bestTrustScore,
    rejectedSources: Array.isArray(evidence.rejectedSources) ? evidence.rejectedSources : [],
    warnings: Array.isArray(evidence.identityWarnings) ? evidence.identityWarnings : [],
    rawResult: result,
  };
}

function plainEnglishFailure(summary) {
  if (!summary) return 'VegAI saved this because something went wrong, but there is not enough detail yet to explain the failure.';
  const subject = summary.productMatch || summary.query || 'this product';
  if (summary.reason === 'exact_source_missing') {
    return `VegAI found possible sources for ${subject}, but it did not trust any of them enough to analyze. This usually means the product name, flavor, size, or source evidence did not match cleanly.`;
  }
  if (summary.reason === 'missing_ingredients' || summary.reason === 'barcode_missing_ingredients') {
    return `VegAI found ${subject}, but the source did not include ingredients. Without ingredients, VegAI should not guess a vegan result.`;
  }
  if (summary.reason === 'barcode_not_found') {
    return `VegAI could read or receive the barcode, but the barcode was not found in the trusted product sources. Manual entry may work, but the product needs better source coverage.`;
  }
  if (summary.reason === 'backend_error' || summary.error) {
    return `The backend reported an error while processing ${subject}. Check the raw backend result and logs if this keeps happening.`;
  }
  if (summary.reason === 'lookup_failed') {
    return `VegAI could not complete the lookup for ${subject}. The likely issue is product-source coverage, unclear naming, or a failed resolver step.`;
  }
  return `VegAI could not confidently complete this request for ${subject}. Review the source evidence and rejected sources to decide whether this needs a verified record or a code/source fix.`;
}

function isFailureItem(item) {
  return Boolean(item?.lastResult?.failure || item?.lastResult?.error || FAILURE_REASONS.has(item?.reason));
}

function searchableText(value) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(searchableText).join(' ');
  if (typeof value === 'object') return Object.values(value).map(searchableText).join(' ');
  return String(value);
}

function matchesSearch(value, query) {
  const needle = String(query || '').trim().toLowerCase();
  if (!needle) return true;
  return searchableText(value).toLowerCase().includes(needle);
}

function matchesQueueFilter(item, filter) {
  if (filter === 'all') return true;
  if (filter === 'needs_review') return ['single_uncertain', 'repeated_uncertainty', 'not_vegan_result'].includes(item.reason);
  if (filter.startsWith('category:')) return item.flagCategory === filter.slice('category:'.length);
  return item.reason === filter;
}

function matchesFailureFilter(item, filter) {
  if (filter === 'all') return true;
  if (filter === 'source') return ['exact_source_missing', 'missing_ingredients'].includes(item.reason);
  if (filter === 'barcode') return ['barcode_missing_ingredients', 'barcode_not_found'].includes(item.reason);
  if (filter === 'backend') return ['backend_error', 'lookup_failed'].includes(item.reason) || Boolean(item?.lastResult?.error);
  return item.reason === filter;
}

function SearchBox({ value, onChange, placeholder, helper }) {
  return (
    <label className="block space-y-1">
      <span className="relative block">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
        <input
          type="search"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-10 pr-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-teal-600/60"
        />
      </span>
      {helper ? <span className="block px-1 text-xs text-slate-500">{helper}</span> : null}
    </label>
  );
}

function WorkCard({ title, actionLabel, onAction, children }) {
  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm shadow-slate-900/5">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-slate-900">{title}</h2>
        {onAction ? (
          <button type="button" onClick={onAction} className="text-sm font-semibold text-teal-700 hover:text-teal-800">
            {actionLabel}
          </button>
        ) : null}
      </div>
      <div className="mt-4 space-y-3">{children}</div>
    </section>
  );
}

function CommandFeedback({ error, message, lastDeletedVerified, onUndoDelete, loading }) {
  if (!error && !message) return null;
  return (
    <div className="space-y-3" aria-live="polite">
      {error ? (
        <p role="alert" className="rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-700">
          {error}
        </p>
      ) : null}
      {message ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-teal-600/20 bg-teal-600/10 p-3 text-sm text-teal-800">
          <p>{message}</p>
          {lastDeletedVerified ? (
            <button
              type="button"
              onClick={onUndoDelete}
              disabled={loading}
              className="rounded-lg border border-teal-500/40 px-3 py-1.5 font-semibold text-teal-900 transition hover:bg-emerald-300/10 disabled:opacity-50"
            >
              Undo Delete
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function MiniWorkItem({ title, meta, body, onClick }) {
  return (
    <button type="button" onClick={onClick} className="w-full rounded-2xl border border-slate-200 bg-white p-4 text-left transition hover:border-slate-300">
      <p className="font-semibold text-slate-900">{title || 'Untitled'}</p>
      {body ? <p className="mt-1 line-clamp-2 text-sm text-slate-400">{body}</p> : null}
      {meta ? <p className="mt-2 text-xs text-slate-500">{meta}</p> : null}
    </button>
  );
}

function HealthRow({ label, value, detail }) {
  const ok = value === 'ok';
  return (
    <div className="flex items-start justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4">
      <div>
        <p className="font-semibold text-slate-900">{label}</p>
        {detail ? <p className="mt-1 text-xs text-slate-500">{detail}</p> : null}
      </div>
      <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${ok ? 'bg-teal-600/10 text-teal-800' : 'bg-amber-400/15 text-amber-200'}`}>
        {value || 'unknown'}
      </span>
    </div>
  );
}

function AnalyticsMetric({ label, value, detail, tone = 'slate', onClick, active = false }) {
  const toneClass = tone === 'emerald'
    ? 'border-teal-600/25 bg-teal-600/10'
    : tone === 'amber'
      ? 'border-amber-400/25 bg-amber-400/10'
      : tone === 'sky'
        ? 'border-sky-400/25 bg-sky-400/10'
        : 'border-slate-200 bg-white';
  const Component = onClick ? 'button' : 'div';
  return (
    <Component
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={`w-full rounded-2xl border p-4 text-left ${toneClass} ${onClick ? 'transition hover:border-teal-500/50 focus:outline-none focus:ring-2 focus:ring-teal-600/30' : ''} ${active ? 'ring-2 ring-teal-600/40' : ''}`}
    >
      <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">{label}</p>
      <p className="mt-2 text-2xl font-bold text-slate-900">{value ?? 0}</p>
      {detail ? <p className="mt-1 text-xs text-slate-500">{detail}</p> : null}
    </Component>
  );
}

function analyticsLabel(value) {
  const labels = {
    app: 'App visit',
    manual: 'Manual analysis',
    product: 'Product lookup',
    scanner: 'Scanner',
    barcode: 'Barcode',
    image: 'Image scan',
    feedback: 'Feedback',
    favorites: 'Favorites',
    history: 'History',
    navigation: 'Navigation',
    page_view: 'Page view',
    tab_view: 'Tab view',
    analysis_submit: 'Analysis submitted',
    analysis_success: 'Analysis succeeded',
    analysis_failure: 'Analysis failed',
    feedback_open: 'Feedback opened',
    feedback_submit: 'Feedback submitted',
    favorite_save: 'Favorite saved',
    favorite_toggle: 'Favorite toggled',
    history_rerun: 'History rerun',
  };
  return labels[value] || value || 'unknown';
}

function timeValue(value) {
  const time = new Date(value || '').getTime();
  return Number.isFinite(time) ? time : 0;
}

function sameLookup(a, b) {
  const left = String(a?.productMatch || a?.query || '').toLowerCase();
  const right = String(b?.productMatch || b?.query || '').toLowerCase();
  return left && right && (left === right || left.includes(right) || right.includes(left));
}

function findNearbyCostEvent(lookup, events = []) {
  const lookupTime = timeValue(lookup?.createdAt);
  if (!lookupTime) return null;
  return (events || [])
    .map((event) => ({ event, distance: Math.abs(timeValue(event.createdAt) - lookupTime) }))
    .filter(({ distance }) => distance <= 5 * 60 * 1000)
    .sort((a, b) => a.distance - b.distance)[0]?.event || null;
}

function analyticsStatusTone(status) {
  if (status === 'vegan') return 'bg-teal-600/10 text-teal-800';
  if (status === 'not-vegan') return 'bg-rose-400/15 text-rose-200';
  if (status === 'maybe-vegan' || status === 'unknown') return 'bg-amber-400/15 text-amber-200';
  return 'bg-slate-800 text-slate-700';
}

function DocLinkCard({ label, detail, href }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="group flex items-start justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-left transition hover:border-teal-600/40 hover:bg-teal-600/10"
    >
      <span>
        <span className="block font-semibold text-slate-900">{label}</span>
        <span className="mt-1 block text-sm leading-5 text-slate-500">{detail}</span>
      </span>
      <ExternalLink className="mt-1 h-4 w-4 shrink-0 text-slate-600 transition group-hover:text-teal-700" />
    </a>
  );
}

function prefillFromVerifiedProduct(row) {
  const sources = Array.isArray(row?.sources) ? row.sources : [];
  const firstSource = sources[0] || {};
  const parsedNotes = parseNotesPayload(row?.notes);

  return {
    ...blankForm,
    name: row?.name || '',
    brand: row?.brand || '',
    aliases: Array.isArray(row?.aliases) ? row.aliases.join('\n') : '',
    ingredients: Array.isArray(row?.ingredients) ? row.ingredients.join('\n') : '',
    status: row?.status || 'vegan',
    sourceUrl: firstSource.url || '',
    sourceType: firstSource.type || firstSource.label || 'manufacturer',
    verificationReason: parsedNotes.verificationReason,
    verifiedBy: row?.verifiedBy || '',
    notes: parsedNotes.notes,
    expiresAt: row?.expiresAt ? String(row.expiresAt).slice(0, 10) : '',
  };
}

function parseRecheckDate(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || '').trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) return null;
  return date;
}

function formatRecheckDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function extendVerifiedRecheckDate(currentValue, days, now = new Date()) {
  const amount = Number(days);
  if (![30, 60, 90].includes(amount)) return String(currentValue || '');
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const current = parseRecheckDate(currentValue);
  const base = current && current > today ? current : today;
  const extended = new Date(base.getFullYear(), base.getMonth(), base.getDate());
  extended.setDate(extended.getDate() + amount);
  return formatRecheckDate(extended);
}

export function verifiedSaveIdentity(id) {
  const rawId = String(id ?? '').trim();
  if (rawId.startsWith('seed-')) {
    return { id: undefined, replacesSeedId: rawId };
  }
  return { id };
}

function RecheckByField({
  value,
  onChange,
  onExtend,
  menuOpen,
  onToggleMenu,
  hint,
  optional = false,
  showDefaultHelp = false,
}) {
  return (
    <div className="space-y-1 text-sm font-semibold">
      <div className="flex items-center justify-between gap-2">
        <label htmlFor="verified-recheck-date">Recheck by{optional ? ' (optional)' : ''}</label>
        <button
          type="button"
          onClick={onToggleMenu}
          aria-expanded={menuOpen}
          className="rounded-lg border border-teal-600/30 bg-teal-600/10 px-2.5 py-1 text-xs font-semibold text-teal-800 transition hover:bg-teal-600/15"
        >
          Extend
        </button>
      </div>
      <input
        id="verified-recheck-date"
        className="w-full rounded-xl border px-3 py-2"
        placeholder="YYYY-MM-DD"
        value={value}
        onChange={onChange}
      />
      {menuOpen ? (
        <div className="flex flex-wrap gap-2 rounded-xl border border-slate-200 bg-slate-50 p-2" aria-label="Extend recheck date">
          {[30, 60, 90].map((days) => (
            <button
              key={days}
              type="button"
              onClick={() => onExtend(days)}
              className="rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 transition hover:border-teal-500 hover:text-teal-800"
            >
              +{days} days
            </button>
          ))}
        </div>
      ) : null}
      {hint ? <span className="block text-xs font-normal text-teal-700">{hint}</span> : null}
      {showDefaultHelp ? (
        <span className="block text-xs font-normal text-slate-500">
          Leave blank for the default window (90 days, same horizon as when name lookups fall out of cache).
          After that date the app re-runs live lookup instead of the fast verified path. Enter none to skip
          expiry (not recommended).
        </span>
      ) : null}
    </div>
  );
}

export function AdminPanel() {
  const [secret, setSecret] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [adminView, setAdminView] = useState('overview');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [verifiedRows, setVerifiedRows] = useState([]);
  const [feedbackItems, setFeedbackItems] = useState([]);
  const [opsEvents, setOpsEvents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [editingVerified, setEditingVerified] = useState(null);
  const [confirmingVerifiedDeleteId, setConfirmingVerifiedDeleteId] = useState(null);
  const [lastDeletedVerified, setLastDeletedVerified] = useState(null);
  const [manualMode, setManualMode] = useState(false);
  const [queueFilter, setQueueFilter] = useState('all');
  const [failureFilter, setFailureFilter] = useState('all');
  const [queueStatusFilter, setQueueStatusFilter] = useState('open');
  const [feedbackFilter, setFeedbackFilter] = useState('open');
  const [verifiedReviewFilter, setVerifiedReviewFilter] = useState('all');
  const [workspaceSearch, setWorkspaceSearch] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewFlagCategory, setReviewFlagCategory] = useState('');
  const [feedbackNotes, setFeedbackNotes] = useState('');
  const [serviceHealth, setServiceHealth] = useState(null);
  const [costActivity, setCostActivity] = useState(null);
  const [analyticsSummary, setAnalyticsSummary] = useState(null);
  const [e2eRuns, setE2eRuns] = useState([]);
  const [selectedE2eRun, setSelectedE2eRun] = useState(null);
  const [expandedE2eCheckId, setExpandedE2eCheckId] = useState(null);
  const [e2eDetailView, setE2eDetailView] = useState('failed');
  const [e2eDetailSuite, setE2eDetailSuite] = useState('');
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [overviewNow, setOverviewNow] = useState(() => new Date());
  const [form, setForm] = useState(blankForm);
  const [recheckExtendOpen, setRecheckExtendOpen] = useState(false);
  const [recheckHint, setRecheckHint] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const canLoad = secret.trim().length > 0;

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [adminView]);

  useEffect(() => {
    let targetId = '';
    if (editingVerified) targetId = 'command-verified-editor';
    else if (selectedFeedback) targetId = 'command-feedback-detail';
    else if (selected || manualMode) targetId = 'command-queue-detail';
    if (!targetId) return;
    window.requestAnimationFrame(() => {
      const target = document.getElementById(targetId);
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      target?.querySelector('input, select, textarea, button')?.focus({ preventScroll: true });
    });
  }, [adminView, editingVerified, manualMode, selected, selectedFeedback]);

  useEffect(() => {
    const onScroll = () => setShowBackToTop(window.scrollY > 400);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (adminView !== 'overview') return undefined;
    setOverviewNow(new Date());
    const timer = window.setInterval(() => setOverviewNow(new Date()), 15000);
    return () => window.clearInterval(timer);
  }, [adminView]);

  const selectedSources = useMemo(() => {
    if (editingVerified) {
      return Array.isArray(editingVerified.sources) ? editingVerified.sources : [];
    }
    const result = selected?.lastResult || {};
    return Array.isArray(result.sources) ? result.sources : [];
  }, [editingVerified, selected]);

  const filteredItems = useMemo(() => {
    const reviewItems = items.filter((item) => !isFailureItem(item));
    return reviewItems.filter((item) => matchesQueueFilter(item, queueFilter) && matchesSearch(item, workspaceSearch));
  }, [items, queueFilter, workspaceSearch]);

  const failureItems = useMemo(() => {
    const failures = items.filter((item) => isFailureItem(item));
    return failures.filter((item) => matchesFailureFilter(item, failureFilter) && matchesSearch(item, workspaceSearch));
  }, [items, failureFilter, workspaceSearch]);

  const reviewItems = useMemo(() => items.filter((item) => !isFailureItem(item)), [items]);
  const allFailureItems = useMemo(() => items.filter((item) => isFailureItem(item)), [items]);
  const searchedOpsEvents = useMemo(
    () => opsEvents.filter((item) => matchesSearch(item, workspaceSearch)),
    [opsEvents, workspaceSearch]
  );
  const searchedFeedbackItems = useMemo(
    () => feedbackItems.filter((item) => matchesSearch(item, workspaceSearch)),
    [feedbackItems, workspaceSearch]
  );
  const searchedVerifiedRows = useMemo(
    () => verifiedRows.filter((row) => {
      const matchesFilter =
        verifiedReviewFilter === 'all' ||
        (verifiedReviewFilter === 'needs_review' && (row.needsReview || row.recordExpired)) ||
        row.reviewStatus === verifiedReviewFilter;
      return matchesFilter && matchesSearch(row, workspaceSearch);
    }),
    [verifiedRows, verifiedReviewFilter, workspaceSearch]
  );
  const searchedE2eRuns = useMemo(
    () => e2eRuns.filter((run) => matchesSearch(run, workspaceSearch)),
    [e2eRuns, workspaceSearch]
  );
  const verifiedCatalogCounts = useMemo(() => ({
    total: verifiedRows.length,
    needsReview: verifiedRows.filter((row) => row.needsReview || row.recordExpired).length,
    expiringSoon: verifiedRows.filter((row) => row.reviewStatus === 'expiring_soon').length,
    current: verifiedRows.filter((row) => row.reviewStatus === 'current').length,
  }), [verifiedRows]);
  async function loadWorkspace(status = queueStatusFilter) {
    setError('');
    setMessage('Refreshing Command...');
    setLoading(true);
    const refreshStartedAt = Date.now();
    try {
      const [queue, feedback, catalog, costs, ops, analytics] = await Promise.all([
        getReviewQueueAPI({ secret, status, limit: 200 }),
        listFeedbackInboxAPI({ secret, status: feedbackFilter, limit: 200 }),
        listVerifiedCatalogAPI({ secret, limit: 300 }),
        getCostControlsAPI({ secret, limit: 12 }),
        listOpsEventsAPI({ secret, status: 'open', limit: 100 }),
        getAnalyticsSummaryAPI({ secret, limit: 8 }),
      ]);
      setItems(queue);
      setFeedbackItems(feedback);
      setVerifiedRows(catalog);
      setCostActivity(costs);
      setOpsEvents(ops);
      setAnalyticsSummary(analytics);
      setAuthenticated(true);
      setMessage(`Command refreshed at ${formatRefreshTime()}.`);
      if (queue.length === 0) setSelected(null);
      if (feedback.length === 0) {
        setSelectedFeedback(null);
        setFeedbackNotes('');
      }
    } catch (err) {
      setError(err.message || 'Could not load admin workspace.');
    } finally {
      await waitForRefreshCue(refreshStartedAt);
      setLoading(false);
    }
  }

  async function loadQueue(status = queueStatusFilter) {
    setError('');
    setMessage('Refreshing queue...');
    setLoading(true);
    const refreshStartedAt = Date.now();
    try {
      const queue = await getReviewQueueAPI({ secret, status, limit: 200 });
      setItems(queue);
      setAuthenticated(true);
      setMessage(`Queue refreshed at ${formatRefreshTime()}.`);
      if (queue.length === 0) setSelected(null);
    } catch (err) {
      setError(err.message || 'Could not load review queue.');
    } finally {
      await waitForRefreshCue(refreshStartedAt);
      setLoading(false);
    }
  }

  async function loadOpsEvents(status = 'open') {
    setError('');
    setMessage('Refreshing errors...');
    setLoading(true);
    const refreshStartedAt = Date.now();
    try {
      const rows = await listOpsEventsAPI({ secret, status, limit: 100 });
      setOpsEvents(rows);
      setMessage(`Errors refreshed at ${formatRefreshTime()}.`);
    } catch (err) {
      setError(err.message || 'Could not load operations events.');
    } finally {
      await waitForRefreshCue(refreshStartedAt);
      setLoading(false);
    }
  }

  async function loadVerifiedCatalog() {
    setError('');
    setMessage('Refreshing catalog...');
    setLoading(true);
    const refreshStartedAt = Date.now();
    try {
      const rows = await listVerifiedCatalogAPI({ secret, limit: 300 });
      setVerifiedRows(rows);
      setMessage(`Catalog refreshed at ${formatRefreshTime()}.`);
    } catch (err) {
      setError(err.message || 'Could not load verified catalog.');
    } finally {
      await waitForRefreshCue(refreshStartedAt);
      setLoading(false);
    }
  }

  async function loadFeedbackInbox(status = feedbackFilter) {
    setError('');
    setMessage('Refreshing feedback...');
    setLoading(true);
    const refreshStartedAt = Date.now();
    try {
      const rows = await listFeedbackInboxAPI({ secret, status, limit: 200 });
      setFeedbackItems(rows);
      setMessage(`Feedback refreshed at ${formatRefreshTime()}.`);
      if (rows.length === 0) {
        setSelectedFeedback(null);
        setFeedbackNotes('');
      }
    } catch (err) {
      setError(err.message || 'Could not load feedback inbox.');
    } finally {
      await waitForRefreshCue(refreshStartedAt);
      setLoading(false);
    }
  }

  async function loadAnalytics() {
    setError('');
    setMessage('Refreshing analytics...');
    setLoading(true);
    const refreshStartedAt = Date.now();
    try {
      const analytics = await getAnalyticsSummaryAPI({ secret, limit: 12 });
      setAnalyticsSummary(analytics);
      setMessage(`Analytics refreshed at ${formatRefreshTime()}.`);
    } catch (err) {
      setError(err.message || 'Could not load analytics.');
    } finally {
      await waitForRefreshCue(refreshStartedAt);
      setLoading(false);
    }
  }

  async function loadE2eResults() {
    setError('');
    setMessage('Refreshing E2E testing results...');
    setLoading(true);
    const refreshStartedAt = Date.now();
    try {
      const rows = await getE2eResultsAPI({ secret, limit: 10 });
      setE2eRuns(rows);
      if (rows.length > 0) {
        const selectedId = selectedE2eRun?.id || rows[0].id;
        const nextId = rows.some((run) => run.id === selectedId) ? selectedId : rows[0].id;
        const detail = await getE2eResultAPI({ secret, id: nextId });
        setSelectedE2eRun(detail);
      } else {
        setSelectedE2eRun(null);
      }
      setMessage(`E2E testing results refreshed at ${formatRefreshTime()}.`);
    } catch (err) {
      setError(err.message || 'Could not load E2E testing results.');
    } finally {
      await waitForRefreshCue(refreshStartedAt);
      setLoading(false);
    }
  }

  async function selectE2eRun(run) {
    if (!run?.id) return;
    setError('');
    setMessage('');
    setExpandedE2eCheckId(null);
    setE2eDetailSuite('');
    try {
      const detail = await getE2eResultAPI({ secret, id: run.id });
      setSelectedE2eRun(detail);
      setE2eDetailView(defaultE2eDetailViewForRun(detail));
    } catch (err) {
      setError(err.message || 'Could not load that E2E run.');
    }
  }

  async function deleteE2eRun(run) {
    if (!run?.id) return;
    const ok = window.confirm(`Delete E2E run #${run.id}? This only removes it from Command history.`);
    if (!ok) return;
    setError('');
    setMessage(`Deleting E2E run #${run.id}...`);
    try {
      await deleteE2eResultAPI({ secret, id: run.id });
      const rows = await getE2eResultsAPI({ secret, limit: 10 });
      setE2eRuns(rows);
      if (selectedE2eRun?.id === run.id) {
        if (rows.length > 0) {
          const detail = await getE2eResultAPI({ secret, id: rows[0].id });
          setSelectedE2eRun(detail);
          setE2eDetailView(defaultE2eDetailViewForRun(detail));
        } else {
          setSelectedE2eRun(null);
          setE2eDetailView('failed');
        }
      }
        setExpandedE2eCheckId(null);
      setE2eDetailSuite('');
      setMessage(`Deleted E2E run #${run.id}.`);
    } catch (err) {
      setError(err.message || 'Could not delete that E2E run.');
    }
  }

  async function clearE2eHistory() {
    const ok = window.confirm('Delete all E2E history from Command? This cannot be undone.');
    if (!ok) return;
    setError('');
    setMessage('Deleting E2E history...');
    try {
      await clearE2eResultsAPI({ secret });
      setE2eRuns([]);
      setSelectedE2eRun(null);
        setExpandedE2eCheckId(null);
      setE2eDetailView('failed');
      setE2eDetailSuite('');
      setMessage('Deleted all E2E history.');
    } catch (err) {
      setError(err.message || 'Could not delete E2E history.');
    }
  }

  async function runServiceChecks() {
    setError('');
    setMessage('Running service checks...');
    setLoading(true);
    const refreshStartedAt = Date.now();
    try {
      const [health, costs] = await Promise.all([
        checkBackendHealthAPI({ deep: true }),
        getCostControlsAPI({ secret, limit: 12 }),
      ]);
      setServiceHealth(health);
      setCostActivity(costs);
      setMessage(`Service checks refreshed at ${formatRefreshTime()}.`);
    } catch (err) {
      setServiceHealth({ status: 'error', message: err.message });
      setError(err.message || 'Could not run service checks.');
    } finally {
      await waitForRefreshCue(refreshStartedAt);
      setLoading(false);
    }
  }

  async function sendSentryTestEvents() {
    setError('');
    setMessage('Sending Sentry test events...');
    setLoading(true);
    try {
      const frontendEventId = captureFrontendMessage('VegAI frontend Sentry test event', {
        source: 'admin_command',
        requestedAt: new Date().toISOString(),
      });
      const backend = await sendBackendSentryTestAPI({ secret });
      const parts = [
        frontendEventId ? 'frontend sent' : 'frontend not configured',
        backend?.sent ? 'backend sent' : 'backend not configured',
      ];
      setMessage(`Sentry test complete: ${parts.join('; ')}.`);
    } catch (err) {
      setError(err.message || 'Could not send Sentry test events.');
    } finally {
      setLoading(false);
    }
  }

  async function switchAdminView(view) {
    setAdminView(view);
    setWorkspaceSearch('');
    setSelected(null);
    setReviewNotes('');
    setSelectedFeedback(null);
    setFeedbackNotes('');
    setEditingVerified(null);
    setSelectedE2eRun(null);
    setConfirmingVerifiedDeleteId(null);
    setManualMode(false);
    setMessage('');
    setError('');
    if (view === 'overview') {
      await loadWorkspace();
      await loadE2eResults();
    } else if (view === 'analytics') {
      await loadAnalytics();
    } else if (view === 'queue' || view === 'failures') {
      await loadQueue();
      if (view === 'failures') await loadOpsEvents();
    } else if (view === 'verified') {
      await loadVerifiedCatalog();
    } else if (view === 'learning') {
      setMessage('Learning Archive ready.');
    } else if (view === 'docs') {
      setMessage('Docs hub ready.');
    } else if (view === 'e2e') {
      await loadE2eResults();
    } else {
      await loadFeedbackInbox();
    }
  }

  function selectItem(item) {
    setSelected(item);
    setSelectedFeedback(null);
    setReviewNotes(item?.adminNotes || '');
    setReviewFlagCategory(item?.flagCategory || '');
    setFeedbackNotes('');
    setEditingVerified(null);
    setManualMode(false);
    setForm(prefillFromItem(item));
    setRecheckExtendOpen(false);
    setRecheckHint('');
    setMessage('');
    setError('');
  }

  function selectVerifiedRow(row) {
    setSelected(null);
    setReviewNotes('');
    setSelectedFeedback(null);
    setFeedbackNotes('');
    setEditingVerified(row);
    setConfirmingVerifiedDeleteId(null);
    setManualMode(false);
    setForm(prefillFromVerifiedProduct(row));
    setRecheckExtendOpen(false);
    setRecheckHint('');
    setMessage('');
    setError('');
  }

  function selectFeedback(item) {
    setSelected(null);
    setReviewNotes('');
    setEditingVerified(null);
    setManualMode(false);
    setSelectedFeedback(item);
    setFeedbackNotes(item?.adminNotes || '');
    setMessage('');
    setError('');
  }

  function startManualProduct() {
    setAdminView('queue');
    setSelected(null);
    setReviewNotes('');
    setSelectedFeedback(null);
    setFeedbackNotes('');
    setEditingVerified(null);
    setManualMode(true);
    setForm(blankForm);
    setRecheckExtendOpen(false);
    setRecheckHint('');
    setMessage('');
    setError('');
  }

  /** Exit the editor without saving (manual entry or queue detail). */
  function closeEditor() {
    setSelected(null);
    setReviewNotes('');
    setSelectedFeedback(null);
    setFeedbackNotes('');
    setEditingVerified(null);
    setConfirmingVerifiedDeleteId(null);
    setManualMode(false);
    setForm(blankForm);
    setRecheckExtendOpen(false);
    setRecheckHint('');
    setMessage('');
    setError('');
  }

  function extendRecheckBy(days) {
    const expiresAt = extendVerifiedRecheckDate(form.expiresAt, days);
    setForm({ ...form, expiresAt });
    setRecheckExtendOpen(false);
    setRecheckHint(`Recheck extended to ${expiresAt} — click Save to apply.`);
  }

  async function changeFeedbackStatus(status) {
    if (!selectedFeedback) return;
    setError('');
    setMessage('');
    try {
      await updateFeedbackStatusAPI({
        secret,
        id: selectedFeedback.id,
        status,
        adminNotes: feedbackNotes,
      });
      setMessage(`Feedback marked ${status}.`);
      await loadFeedbackInbox(feedbackFilter);
      setSelectedFeedback(null);
      setFeedbackNotes('');
    } catch (err) {
      setError(err.message || 'Could not update feedback item.');
    }
  }

  async function applySelectedFeedbackCorrection() {
    if (!selectedFeedback) return;
    setError('');
    setMessage('');
    try {
      const updated = await applyFeedbackCorrectionAPI({
        secret,
        id: selectedFeedback.id,
        adminNotes: feedbackNotes,
      });
      setSelectedFeedback(updated);
      setFeedbackNotes(updated?.adminNotes || '');
      const result = updated?.correctionResult;
      setMessage(
        result?.applied
          ? `Correction applied for ${result.ingredient}.`
          : `Correction not applied: ${result?.reason || 'unknown'}.`,
      );
      await loadFeedbackInbox(feedbackFilter);
    } catch (err) {
      setError(err.message || 'Could not apply correction.');
    }
  }

  function logout() {
    setSecret('');
    setAuthenticated(false);
    setSettingsOpen(false);
    setItems([]);
    setVerifiedRows([]);
    setFeedbackItems([]);
    setOpsEvents([]);
    setCostActivity(null);
    setAdminView('queue');
    setSelected(null);
    setReviewNotes('');
    setSelectedFeedback(null);
    setFeedbackNotes('');
    setEditingVerified(null);
    setManualMode(false);
    setMessage('');
    setError('');
  }

  async function saveVerified() {
    setError('');
    setMessage('');
    try {
      if (!isValidVerificationReason(form.verificationReason)) {
        setError(
          'Enter a verification reason (why this status), or type N/A if there is none. Blank, spaces-only, or non-text gibberish is not allowed.',
        );
        return;
      }
      const sources = form.sourceUrl.trim()
        ? [{
            type: form.sourceType.trim() || 'manufacturer',
            url: form.sourceUrl.trim(),
            title: form.name.trim() || form.sourceUrl.trim(),
            name: form.name.trim() || undefined,
          }]
        : selectedSources;
      const replacingSeed = String(editingVerified?.id || '').startsWith('seed-');
      await saveVerifiedProductAPI({
        secret,
        product: {
          ...verifiedSaveIdentity(editingVerified?.id),
          reviewQueueId: selected?.id,
          name: form.name,
          brand: form.brand,
          aliases: splitLines(form.aliases),
          ingredients: splitLines(form.ingredients),
          status: form.status,
          sources,
          verifiedBy: form.verifiedBy || 'admin',
          verificationMethod: 'manual_admin_ui',
          notes: buildNotesPayload(form.verificationReason, form.notes),
          expiresAt: form.expiresAt,
        },
      });
      const saveMessage = (
        replacingSeed
          ? `Saved "${form.name}" and replaced the old seed record.`
          : `Saved "${form.name}" as verified ${form.status}.`
      );
      setSelected(null);
      setEditingVerified(null);
      setConfirmingVerifiedDeleteId(null);
      setManualMode(false);
      setRecheckExtendOpen(false);
      setRecheckHint('');
      if (adminView === 'verified') {
        await loadVerifiedCatalog();
      } else {
        await loadQueue();
      }
      setMessage(saveMessage);
    } catch (err) {
      setError(err.message || 'Could not save verified product.');
    }
  }

  async function deleteVerified() {
    if (!editingVerified?.id) return;
    const productName = editingVerified.name || 'this verified product';
    if (confirmingVerifiedDeleteId !== editingVerified.id) {
      setConfirmingVerifiedDeleteId(editingVerified.id);
      setError('');
      setMessage(`Click Confirm Delete to remove "${productName}".`);
      return;
    }
    setError('');
    setMessage(`Deleting "${productName}"...`);
    setLoading(true);
    const refreshStartedAt = Date.now();
    try {
      const deleted = await deleteVerifiedProductAPI({ secret, id: editingVerified.id });
      setVerifiedRows((rows) => rows.filter((row) => row.id !== editingVerified.id));
      setEditingVerified(null);
      setConfirmingVerifiedDeleteId(null);
      setForm(blankForm);
      setLastDeletedVerified(deleted);
      setMessage(`Deleted "${productName}" from the verified catalog.`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      await loadVerifiedCatalog();
    } catch (err) {
      setError(err.message || 'Could not delete verified product.');
    } finally {
      await waitForRefreshCue(refreshStartedAt);
      setLoading(false);
    }
  }

  async function undoVerifiedDelete() {
    if (!lastDeletedVerified) return;
    const productName = lastDeletedVerified.name || 'verified product';
    setError('');
    setMessage(`Restoring "${productName}"...`);
    setLoading(true);
    const refreshStartedAt = Date.now();
    try {
      await restoreVerifiedProductAPI({ secret, deleted: lastDeletedVerified });
      setLastDeletedVerified(null);
      setMessage(`Restored "${productName}" to the verified catalog.`);
      await loadVerifiedCatalog();
    } catch (err) {
      setError(err.message || 'Could not undo verified product deletion.');
    } finally {
      await waitForRefreshCue(refreshStartedAt);
      setLoading(false);
    }
  }

  async function changeReviewItemStatus(item, status, label, { closeAfter = true } = {}) {
    if (!item) return;
    setError('');
    setMessage('');
    try {
      const updated = await updateReviewQueueStatusAPI({ secret, id: item.id, status, adminNotes: reviewNotes });
      setMessage(`${label || 'Updated'} "${reviewItemTitle(item)}".`);
      if (selected?.id === item.id && !closeAfter) {
        setSelected(updated?.id ? updated : { ...item, status, adminNotes: reviewNotes });
      }
      if (selected?.id === item.id && closeAfter) {
        setSelected(null);
        setReviewNotes('');
      }
      await loadQueue(queueStatusFilter);
    } catch (err) {
      setError(err.message || 'Could not update review item.');
    }
  }

  async function saveReviewFlagCategory() {
    if (!selected || !reviewFlagCategory) return;
    setError('');
    setMessage('');
    try {
      const updated = await updateReviewQueueCategoryAPI({
        secret,
        id: selected.id,
        flagCategory: reviewFlagCategory,
      });
      setSelected(updated);
      setItems((current) => current.map((item) => item.id === updated.id ? updated : item));
      setMessage(`Saved category "${flagCategoryLabel(reviewFlagCategory)}".`);
    } catch (err) {
      setError(err.message || 'Could not save flag category.');
    }
  }

  async function dismissSelected() {
    if (!selected) return;
    await changeReviewItemStatus(selected, 'dismissed', 'Cleared');
  }

  async function dismissQueueItem(item, event) {
    event?.stopPropagation();
    if (!item) return;
    await changeReviewItemStatus(item, 'dismissed', 'Cleared');
  }

  async function dismissOpsEvent(item, event) {
    event?.stopPropagation();
    if (!item) return;
    setError('');
    setMessage('');
    try {
      await updateOpsEventStatusAPI({ secret, id: item.id, status: 'dismissed' });
      setMessage(`Cleared "${item.code || item.category}".`);
      await loadOpsEvents();
    } catch (err) {
      setError(err.message || 'Could not clear operations event.');
    }
  }

  async function saveReviewNotes() {
    if (!selected) return;
    await changeReviewItemStatus(selected, selected.status || queueStatusFilter || 'open', 'Saved notes for', { closeAfter: false });
  }

  function handleLogin(event) {
    event.preventDefault();
    if (!canLoad || loading) return;
    (async () => {
      await loadWorkspace();
      await loadE2eResults();
    })();
  }

  const latestE2eRun = selectedE2eRun || e2eRuns[0] || null;
  const overviewQuote = quoteOfTheDay(overviewNow);
  const openErrorCount = allFailureItems.length + opsEvents.length;
  const openReviewCount = reviewItems.length;
  const openFeedbackCount = feedbackItems.length;
  const searches24h = analyticsSummary?.productLookups?.total24h ?? 0;
  const sourceGapCount = analyticsSummary?.productLookups?.sourceGapItems?.length ?? 0;
  const recentLookups = analyticsSummary?.productLookups?.recentLookups || [];
  const overviewPriority = openErrorCount
    ? {
      title: 'Start with errors.',
      detail: 'Something failed for a user, source, or system path. Clear errors before cleanup work.',
    }
    : (latestE2eRun && latestE2eRun.status !== 'pass')
      ? {
        title: 'Staging checks need a look.',
        detail: 'The latest live E2E run did not fully pass. Open E2E, review failures, then fix or update the benchmark.',
      }
      : openReviewCount
        ? {
          title: 'Review product decisions.',
          detail: 'The review queue has items that can improve future searches once verified.',
        }
        : openFeedbackCount
          ? {
            title: 'Check feedback.',
            detail: 'Users left notes that still need an owner decision.',
          }
          : {
            title: 'You are clear for now.',
            detail: 'No open errors, reviews, or feedback. Scan progress below, then use Analytics or E2E if you want deeper signal.',
          };

  const selectedE2eChecks = e2eCheckRows(selectedE2eRun, e2eDetailView, e2eDetailSuite);
  const selectedE2eHasCheckDetails = Array.isArray(selectedE2eRun?.checks) && selectedE2eRun.checks.length > 0;
  const selectedE2eSkipBreakdown = e2eSkipBreakdown(selectedE2eRun);
  const selectedE2eSuite = (selectedE2eRun?.suites || []).find((suite) => suite.name === e2eDetailSuite);
  const selectedE2eMissingFailureDetails = selectedE2eHasCheckDetails && selectedE2eChecks.length === 0 && (
    (e2eDetailView === 'failed' && Number(selectedE2eRun?.summary?.failed || 0) > 0) ||
    (e2eDetailView === 'suite' && Number(selectedE2eSuite?.failed || 0) > 0)
  );

  if (!authenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#eceff3] p-4 text-slate-800">
        <form
          onSubmit={handleLogin}
          className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-6 shadow-sm shadow-slate-900/5"
        >
          <div className="flex items-center gap-2 text-teal-700">
            <ShieldCheck className="h-5 w-5" />
            <span className="text-sm font-bold uppercase tracking-[0.18em] text-teal-700">VegAI Admin</span>
          </div>
          <h1 className="mt-2 text-3xl font-bold text-slate-900">Admin Login</h1>
          <p className="mt-2 text-sm text-slate-500">
            Enter your admin secret once to open Command, the daily ops control center.
          </p>
          <label className="mt-5 block space-y-2 text-sm font-semibold text-slate-700">
            Admin Secret
            <input
              type="password"
              value={secret}
              onChange={(event) => setSecret(event.target.value)}
              className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-teal-600 focus:ring-4 focus:ring-teal-600/15"
              placeholder="Paste your admin secret"
            />
          </label>
          <button
            type="submit"
            disabled={!canLoad || loading}
            className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-teal-700 px-4 py-2 font-semibold text-white transition hover:bg-teal-600 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Log In
          </button>
          {error && <p className="mt-4 rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-700">{error}</p>}
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#eceff3] text-slate-800">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle_at_top,rgba(15,118,110,0.05),transparent_40%)]" />
      <div className="relative mx-auto flex min-h-screen max-w-[1440px]">
        <aside className="sticky top-0 hidden h-screen w-56 shrink-0 flex-col border-r border-slate-200 bg-white/90 px-3 py-5 lg:flex">
          <div className="px-2">
            <div className="flex items-center gap-2 text-teal-700">
              <ShieldCheck className="h-4 w-4" />
              <span className="text-xs font-bold uppercase tracking-[0.16em]">VegAI Admin</span>
            </div>
            <p className="mt-3 text-xl font-bold text-slate-900">Command</p>
            <p className="mt-1 text-xs leading-5 text-slate-500">Daily work hub</p>
          </div>
          <nav className="mt-6 flex flex-1 flex-col gap-1 overflow-y-auto pb-4">
            {[
              ['overview', 'Overview', LayoutDashboard, null],
              ['queue', 'Review', ListChecks, reviewItems.length || null],
              ['failures', 'Errors', AlertCircle, (allFailureItems.length + opsEvents.length) || null],
              ['analytics', 'Analytics', BarChart3, null],
              ['e2e', 'E2E', CheckCircle, null],
              ['verified', 'Verified', Package, verifiedCatalogCounts.needsReview || null],
              ['feedback', 'Feedback', MessageSquare, feedbackItems.length || null],
              ['learning', 'Learning', Archive, null],
              ['docs', 'Docs', BookOpen, null],
            ].map(([view, label, Icon, count]) => (
              <button
                key={view}
                type="button"
                onClick={() => switchAdminView(view)}
                className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-semibold transition ${
                  adminView === view
                    ? 'bg-teal-600/10 text-teal-800 ring-1 ring-teal-600/20'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                <Icon className="h-4 w-4 shrink-0 opacity-80" aria-hidden="true" />
                <span className="flex-1">{label}</span>
                {count ? <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-bold text-slate-700">{count}</span> : null}
              </button>
            ))}
          </nav>
          <div className="mt-auto space-y-2 border-t border-slate-200 pt-4">
            <button
              type="button"
              onClick={() => {
                setSettingsOpen(true);
                runServiceChecks();
              }}
              className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-semibold text-slate-600 transition hover:bg-slate-100 hover:text-slate-900"
            >
              <Settings className="h-4 w-4" />
              Settings
            </button>
            <button
              type="button"
              onClick={logout}
              className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-semibold text-slate-600 transition hover:bg-red-50 hover:text-red-700"
            >
              <LogOut className="h-4 w-4" />
              Log out
            </button>
          </div>
        </aside>

        <div className="relative flex min-w-0 flex-1 flex-col gap-6 p-4 sm:p-6 lg:p-8">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-teal-700 lg:hidden">VegAI Admin</p>
              <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900">
                {adminView === 'overview' ? 'Overview'
                  : adminView === 'queue' ? 'Review queue'
                    : adminView === 'failures' ? 'Errors'
                      : adminView === 'analytics' ? 'Analytics'
                        : adminView === 'e2e' ? 'E2E'
                          : adminView === 'verified' ? 'Verified catalog'
                            : adminView === 'feedback' ? 'Feedback'
                              : adminView === 'learning' ? 'Learning Archive'
                                : 'Docs'}
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
                {adminView === 'overview'
                  ? 'Start with what needs action. Use the left menu when you want another room.'
                  : 'Search and refresh stay up top. Switch rooms anytime from the left menu.'}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex gap-2 lg:hidden">
                <button
                  type="button"
                  onClick={() => {
                    setSettingsOpen(true);
                    runServiceChecks();
                  }}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500"
                  aria-label="Open Command settings"
                >
                  <Settings className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={logout}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-500"
                >
                  <LogOut className="h-4 w-4" />
                  Log out
                </button>
              </div>
              {adminView !== 'docs' && adminView !== 'learning' && adminView !== 'e2e' && adminView !== 'overview' && adminView !== 'analytics' && (
                <button
                  type="button"
                  onClick={startManualProduct}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 font-semibold text-slate-900 transition hover:bg-slate-100"
                >
                  <Plus className="h-4 w-4" />
                  New Verified Product
                </button>
              )}
              <button
                type="button"
                onClick={async () => {
                  if (adminView === 'overview') {
                    await loadWorkspace();
                    return loadE2eResults();
                  }
                  if (adminView === 'analytics') return loadAnalytics();
                  if (adminView === 'verified') return loadVerifiedCatalog();
                  if (adminView === 'feedback') return loadFeedbackInbox();
                  if (adminView === 'learning') return setMessage('Learning Archive reloads when you open the tab.');
                  if (adminView === 'docs') return setMessage('Docs hub ready.');
                  if (adminView === 'e2e') return loadE2eResults();
                  return loadQueue();
                }}
                disabled={!canLoad || loading}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-teal-600/30 bg-teal-600/10 px-4 py-2.5 font-semibold text-teal-800 transition hover:bg-teal-600/15 disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>

          <div className="flex gap-2 overflow-x-auto pb-1 lg:hidden">
            {[
              ['overview', 'Overview'],
              ['queue', 'Review'],
              ['failures', 'Errors'],
              ['analytics', 'Analytics'],
              ['e2e', 'E2E'],
              ['verified', 'Verified'],
              ['feedback', 'Feedback'],
              ['learning', 'Learning'],
              ['docs', 'Docs'],
            ].map(([view, label]) => (
              <button
                key={view}
                type="button"
                onClick={() => switchAdminView(view)}
                className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-bold transition ${
                  adminView === view ? 'bg-teal-700 text-white' : 'bg-white text-slate-600 ring-1 ring-slate-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {adminView !== 'overview' ? (
            <SearchBox
              value={workspaceSearch}
              onChange={setWorkspaceSearch}
              placeholder={`Search ${adminView === 'learning' ? 'lessons, products, or tags' : adminView === 'verified' ? 'verified catalog' : adminView === 'feedback' ? 'feedback' : adminView === 'failures' ? 'errors' : adminView === 'docs' ? 'docs' : adminView === 'e2e' ? 'E2E runs' : adminView === 'analytics' ? 'analytics' : 'review queue'}`}
              helper={
                adminView === 'docs'
                  ? 'Searches doc titles and descriptions in this tab.'
                  : adminView === 'e2e'
                    ? 'Searches recent live test runs, suites, and summarized failures.'
                    : adminView === 'analytics'
                      ? 'Use refresh to reload searches, gaps, costs, and traffic.'
                      : 'Searches the currently loaded records in this tab.'
              }
            />
          ) : null}

        {adminView === 'overview' ? (
          <div className="grid gap-6">
            <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm shadow-slate-900/5">
              <div className="grid lg:grid-cols-[minmax(0,1.35fr)_minmax(0,0.85fr)]">
                <div className="grid gap-6 border-b border-slate-200 p-6 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start lg:border-b-0 lg:border-r">
                  <div className="max-w-xl">
                    <p className="text-xs font-bold uppercase tracking-wide text-teal-700">Today</p>
                    <h2 className="mt-2 text-xl font-bold text-slate-900 sm:text-2xl">{overviewPriority.title}</h2>
                    <p className="mt-2 text-sm leading-6 text-slate-500">{overviewPriority.detail}</p>
                  </div>
                  <div className="grid grid-cols-3 gap-2 sm:w-44 sm:grid-cols-1">
                    <button type="button" onClick={() => switchAdminView('failures')} className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-left transition hover:border-amber-300/50">
                      <span className="block text-[11px] font-bold uppercase tracking-wide text-slate-500">Errors</span>
                      <span className="mt-0.5 block text-xl font-bold text-slate-900">{openErrorCount}</span>
                    </button>
                    <button type="button" onClick={() => switchAdminView('queue')} className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-left transition hover:border-teal-500/50">
                      <span className="block text-[11px] font-bold uppercase tracking-wide text-slate-500">Review</span>
                      <span className="mt-0.5 block text-xl font-bold text-slate-900">{openReviewCount}</span>
                    </button>
                    <button type="button" onClick={() => switchAdminView('feedback')} className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-left transition hover:border-sky-300/50">
                      <span className="block text-[11px] font-bold uppercase tracking-wide text-slate-500">Feedback</span>
                      <span className="mt-0.5 block text-xl font-bold text-slate-900">{openFeedbackCount}</span>
                    </button>
                  </div>
                </div>
                <div className="bg-slate-800 px-6 py-6 text-left">
                  <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-teal-300">Local time</p>
                  <p className="mt-2 text-sm font-medium text-slate-300">{formatOverviewDate(overviewNow)}</p>
                  <p className="mt-0.5 text-xl font-bold tracking-tight text-white">{formatOverviewClock(overviewNow)}</p>
                  <p className="mt-5 text-sm font-medium leading-6 text-slate-200">“{overviewQuote.text}”</p>
                  <p className="mt-2 text-xs leading-5 text-slate-400">— {overviewQuote.source} · {overviewQuote.kind}</p>
                </div>
              </div>
            </section>

            <button
              type="button"
              onClick={() => switchAdminView('e2e')}
              className="w-full rounded-2xl border border-slate-200 bg-white p-4 text-left transition hover:border-teal-500/40"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs font-bold uppercase tracking-wide text-teal-700">Staging health</p>
                  {latestE2eRun ? (
                    <>
                      <p className="mt-1 font-semibold text-slate-900">{e2eVerdictHeadline(latestE2eRun)}</p>
                      <p className="mt-1 text-xs text-slate-500">
                        {formatDateTime(latestE2eRun.finishedAt) || 'No timestamp'} · {latestE2eRun.environment || 'local'} · {latestE2eRun.summary?.passed ?? 0}/{latestE2eRun.summary?.total ?? 0} passed
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="mt-1 font-semibold text-slate-900">No live E2E run saved yet</p>
                      <p className="mt-1 text-xs text-slate-500">Open E2E when you want to run or review staging visitor checks.</p>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {latestE2eRun ? (
                    <span className={`rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${e2eStatusClass(latestE2eRun.status)}`}>
                      {latestE2eRun.status === 'pass' ? 'PASS' : 'FAIL'}
                    </span>
                  ) : null}
                  <span className="text-xs font-semibold text-teal-700">Open E2E</span>
                </div>
              </div>
            </button>

            <div className="grid gap-6 lg:grid-cols-3">
              <WorkCard title="Errors to clear" actionLabel="Open errors" onAction={() => switchAdminView('failures')}>
                {opsEvents.slice(0, 2).map((item) => (
                  <MiniWorkItem
                    key={`ops-${item.id}`}
                    title={item.code || item.category}
                    body={item.message}
                    meta={`${item.severity || 'system'} · ${formatDateTime(item.updatedAt || item.createdAt) || 'No timestamp'}`}
                    onClick={() => switchAdminView('failures')}
                  />
                ))}
                {allFailureItems.slice(0, 2).map((item) => (
                  <MiniWorkItem
                    key={item.id}
                    title={reviewItemTitle(item)}
                    meta={`${item.reason} · ${formatDateTime(item.updatedAt || item.createdAt) || 'No timestamp'}`}
                    onClick={() => { setAdminView('failures'); selectItem(item); }}
                  />
                ))}
                {openErrorCount === 0 ? (
                  <p className="rounded-2xl border border-dashed border-slate-300 p-4 text-sm text-slate-500">No open errors.</p>
                ) : null}
              </WorkCard>

              <WorkCard title="Review queue" actionLabel="Open queue" onAction={() => switchAdminView('queue')}>
                {reviewItems.slice(0, 3).map((item) => (
                  <MiniWorkItem
                    key={item.id}
                    title={reviewItemTitle(item)}
                    meta={`${item.reason} · ${formatDateTime(item.updatedAt || item.createdAt) || 'No timestamp'}`}
                    onClick={() => { setAdminView('queue'); selectItem(item); }}
                  />
                ))}
                {openReviewCount === 0 ? (
                  <p className="rounded-2xl border border-dashed border-slate-300 p-4 text-sm text-slate-500">No review work waiting.</p>
                ) : null}
              </WorkCard>

              <WorkCard title="Feedback" actionLabel="Open feedback" onAction={() => switchAdminView('feedback')}>
                {feedbackItems.slice(0, 3).map((item) => (
                  <MiniWorkItem
                    key={item.id}
                    title={item.category || 'Feedback'}
                    body={item.message}
                    meta={formatDateTime(item.updatedAt || item.createdAt) || 'No timestamp'}
                    onClick={() => switchAdminView('feedback')}
                  />
                ))}
                {openFeedbackCount === 0 ? (
                  <p className="rounded-2xl border border-dashed border-slate-300 p-4 text-sm text-slate-500">No open feedback.</p>
                ) : null}
              </WorkCard>
            </div>

            <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm shadow-slate-900/5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-teal-700">Progress signal</p>
                  <p className="mt-1 text-sm text-slate-400">Quick picture of recent activity. Open Analytics for the full story.</p>
                </div>
                <button type="button" onClick={() => switchAdminView('analytics')} className="text-sm font-semibold text-teal-700 hover:text-teal-800">
                  Open Analytics
                </button>
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <AnalyticsMetric label="Searches (24h)" value={searches24h} detail="Product checks" tone="emerald" onClick={() => switchAdminView('analytics')} />
                <AnalyticsMetric label="Source gaps" value={sourceGapCount} detail="Weak or missing sources" tone="amber" onClick={() => switchAdminView('analytics')} />
                <AnalyticsMetric label="AI calls (24h)" value={analyticsSummary?.costs?.allowed24h ?? 0} detail={`${analyticsSummary?.costs?.blocked24h ?? 0} blocked`} onClick={() => switchAdminView('analytics')} />
                <AnalyticsMetric label="Visits" value={analyticsSummary?.app?.pageViews ?? 0} detail="Traffic signal" tone="sky" onClick={() => switchAdminView('analytics')} />
              </div>
              {recentLookups.length > 0 ? (
                <div className="mt-4 space-y-2">
                  <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Latest searches</p>
                  {recentLookups.slice(0, 5).map((lookup) => (
                    <button
                      key={`${lookup.query}-${lookup.productMatch}-${lookup.createdAt}`}
                      type="button"
                      onClick={() => switchAdminView('analytics')}
                      className="flex w-full items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-left transition hover:border-slate-300"
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-semibold text-slate-900">{lookup.productMatch || lookup.query || 'Unknown product'}</span>
                        <span className="mt-0.5 block text-xs text-slate-500">{formatDateTime(lookup.createdAt) || 'No timestamp'} · {lookup.resolvedVia || 'unknown source'}</span>
                      </span>
                      <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${analyticsStatusTone(lookup.status)}`}>
                        {lookup.status || 'unknown'}
                      </span>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="mt-4 rounded-2xl border border-dashed border-slate-300 p-4 text-sm text-slate-500">No recent searches recorded yet.</p>
              )}
            </section>

            {sourceGapCount > 0 ? (
              <WorkCard title="Source gaps needing attention" actionLabel="Open Analytics" onAction={() => switchAdminView('analytics')}>
                {(analyticsSummary?.productLookups?.sourceGapItems || []).slice(0, 4).map((item) => (
                  <div key={`${item.query}-${item.reason}-${item.updatedAt}`} className="rounded-2xl border border-amber-400/20 bg-amber-400/10 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-900">{item.productMatch || item.query || 'Unknown product'}</p>
                        <p className="mt-1 text-xs text-amber-900/70">{item.query || 'No query'} · {formatDateTime(item.updatedAt) || 'No timestamp'}</p>
                      </div>
                      <span className="rounded-full bg-amber-400/15 px-2.5 py-1 text-xs font-bold text-amber-900">{item.reason || 'unknown'}</span>
                    </div>
                  </div>
                ))}
              </WorkCard>
            ) : null}

            {verifiedCatalogCounts.needsReview > 0 ? (
              <button
                type="button"
                onClick={() => switchAdminView('verified')}
                className="w-full rounded-2xl border border-amber-400/20 bg-amber-400/10 p-4 text-left transition hover:border-amber-300/40"
              >
                <p className="text-xs font-bold uppercase tracking-wide text-amber-900">Verified catalog</p>
                <p className="mt-1 font-semibold text-slate-900">{verifiedCatalogCounts.needsReview} verified product{verifiedCatalogCounts.needsReview === 1 ? '' : 's'} need re-review</p>
                <p className="mt-1 text-xs text-amber-900/70">Open Verified catalog when you are ready to refresh those records.</p>
              </button>
            ) : null}
          </div>
        ) : adminView === 'analytics' ? (
          <div className="grid gap-4">
            <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm shadow-slate-900/5">
              <div className="flex items-start gap-3">
                <div className="rounded-2xl border border-teal-600/30 bg-teal-600/10 p-3 text-teal-800">
                  <BarChart3 className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-teal-700">Company-grade signal</p>
                  <h2 className="mt-2 text-2xl font-bold text-slate-900">Analytics</h2>
                  <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-400">
                    What people tried, what VegAI returned, what still needs review, and whether usage and AI cost look healthy.
                  </p>
                  {analyticsSummary?.generatedAt ? (
                    <p className="mt-2 text-xs text-slate-500">Generated {formatDateTime(analyticsSummary.generatedAt)}</p>
                  ) : null}
                </div>
              </div>
            </section>

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <AnalyticsMetric label="Searches" value={analyticsSummary?.productLookups?.total24h ?? 0} detail="Product checks in 24h" tone="emerald" />
              <AnalyticsMetric label="Needs Review" value={(analyticsSummary?.productLookups?.sourceGapItems?.length ?? 0) + (analyticsSummary?.feedback?.open ?? 0) + (analyticsSummary?.operations?.open ?? 0)} detail="Source gaps, feedback, errors" tone="amber" />
              <AnalyticsMetric label="AI Calls" value={analyticsSummary?.costs?.allowed24h ?? 0} detail={`${analyticsSummary?.costs?.blocked24h ?? 0} blocked`} />
              <AnalyticsMetric label="Visits" value={analyticsSummary?.app?.pageViews ?? 0} detail="Traffic signal" tone="sky" />
            </div>

            <WorkCard title="Recent Search Timeline">
              {(analyticsSummary?.productLookups?.recentLookups || []).map((lookup) => {
                const gap = (analyticsSummary?.productLookups?.sourceGapItems || []).find((item) => sameLookup(item, lookup));
                const aiEvent = findNearbyCostEvent(lookup, analyticsSummary?.costs?.recentEvents);
                return (
                  <details key={`${lookup.query}-${lookup.productMatch}-${lookup.createdAt}`} className="rounded-2xl border border-slate-200 bg-white p-4">
                    <summary className="flex cursor-pointer list-none items-start justify-between gap-3">
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-semibold text-slate-900">{lookup.productMatch || lookup.query || 'Unknown product'}</span>
                        <span className="mt-1 block text-xs text-slate-500">
                          Search: {lookup.query || 'unknown'} · {formatDateTime(lookup.createdAt) || 'No timestamp'}
                        </span>
                        <span className="mt-2 block text-xs text-slate-400">
                          Source: {lookup.resolvedVia || 'unknown'} · Confidence: {lookup.confidence ?? 'n/a'} · Trust: {lookup.sourceEvidence?.bestTrustScore ?? 'n/a'}
                        </span>
                      </span>
                      <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${analyticsStatusTone(lookup.status)}`}>
                        {lookup.status || 'unknown'}
                      </span>
                    </summary>
                    <div className="mt-4 grid gap-3 border-t border-slate-200 pt-4 text-xs text-slate-400 md:grid-cols-3">
                      <div>
                        <p className="font-bold uppercase tracking-[0.14em] text-slate-500">Result</p>
                        <p className="mt-1">Matched: {lookup.productMatch || 'none'}</p>
                        <p>Status: {lookup.status || 'unknown'}</p>
                      </div>
                      <div>
                        <p className="font-bold uppercase tracking-[0.14em] text-slate-500">Source Quality</p>
                        <p className="mt-1">Resolved via: {lookup.resolvedVia || 'unknown'}</p>
                        <p>Best trust: {lookup.sourceEvidence?.bestTrustScore ?? 'n/a'}</p>
                      </div>
                      <div>
                        <p className="font-bold uppercase tracking-[0.14em] text-slate-500">Next Action</p>
                        <p className="mt-1">{gap ? `Review queue: ${gap.reason}` : 'No review item recorded.'}</p>
                        <p>{aiEvent ? `AI: ${aiEvent.allowed ? 'allowed' : 'blocked'} (${aiEvent.reason || 'ok'})` : 'No nearby AI cost event.'}</p>
                      </div>
                    </div>
                  </details>
                );
              })}
              {(!analyticsSummary?.productLookups?.recentLookups || analyticsSummary.productLookups.recentLookups.length === 0) && (
                <p className="rounded-2xl border border-dashed border-slate-300 p-4 text-sm text-slate-500">No product searches recorded yet.</p>
              )}
            </WorkCard>

            <div className="grid gap-4 lg:grid-cols-2">
              <WorkCard title="Needs Attention">
                {(analyticsSummary?.productLookups?.sourceGapItems || []).slice(0, 6).map((item) => (
                  <div key={`${item.query}-${item.reason}-${item.updatedAt}`} className="rounded-2xl border border-amber-400/20 bg-amber-400/10 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-900">{item.productMatch || item.query || 'Unknown product'}</p>
                        <p className="mt-1 text-xs text-amber-900/70">{item.query || 'No query'} · {formatDateTime(item.updatedAt) || 'No timestamp'}</p>
                      </div>
                      <span className="rounded-full bg-amber-400/15 px-2.5 py-1 text-xs font-bold text-amber-900">{item.reason || 'unknown'}</span>
                    </div>
                  </div>
                ))}
                {(!analyticsSummary?.productLookups?.sourceGapItems || analyticsSummary.productLookups.sourceGapItems.length === 0) && (
                  <p className="rounded-2xl border border-dashed border-slate-300 p-4 text-sm text-slate-500">No source gaps need attention in the current window.</p>
                )}
              </WorkCard>

              <WorkCard title="Patterns">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Common Products</p>
                    <div className="mt-2 space-y-2">
                      {(analyticsSummary?.productLookups?.commonProducts || []).slice(0, 4).map((item) => (
                        <div key={`${item.product}-${item.count}-${item.lastSeen}`} className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-3">
                          <p className="min-w-0 truncate text-sm font-semibold text-slate-900">{item.product || 'Unknown product'}</p>
                          <span className="rounded-full bg-teal-600/10 px-2.5 py-1 text-xs font-bold text-teal-800">{item.count}</span>
                        </div>
                      ))}
                      {!(analyticsSummary?.productLookups?.commonProducts || []).length ? (
                        <p className="rounded-2xl border border-dashed border-slate-300 p-3 text-xs text-slate-500">No repeated products yet.</p>
                      ) : null}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Feature Use</p>
                    <div className="mt-2 space-y-2">
                      {(analyticsSummary?.app?.byFeature || []).slice(0, 4).map((item) => (
                        <div key={`${item.feature}-${item.count}-${item.lastSeen}`} className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-3">
                          <p className="min-w-0 truncate text-sm font-semibold text-slate-900">{analyticsLabel(item.feature)}</p>
                          <span className="rounded-full bg-sky-400/15 px-2.5 py-1 text-xs font-bold text-sky-800">{item.count}</span>
                        </div>
                      ))}
                      {!(analyticsSummary?.app?.byFeature || []).length ? (
                        <p className="rounded-2xl border border-dashed border-slate-300 p-3 text-xs text-slate-500">No feature use yet.</p>
                      ) : null}
                    </div>
                  </div>
                </div>
              </WorkCard>
            </div>
          </div>
        ) : adminView === 'e2e' ? (
          <div className="grid gap-4">
            <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm shadow-slate-900/5">
              {selectedE2eRun ? (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-teal-700">E2E Testing Results</p>
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <h2 className="text-2xl font-bold text-slate-900">{e2eVerdictHeadline(selectedE2eRun)}</h2>
                    <span className={`rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${e2eStatusClass(selectedE2eRun.status)}`}>
                      {selectedE2eRun.status === 'pass' ? 'PASS' : 'FAIL'}
                    </span>
                    <span className={`rounded-full border px-2.5 py-1 text-[11px] font-bold ${e2eImpactClass(selectedE2eRun.userImpact)}`}>
                      {e2eImpactLabel(selectedE2eRun.userImpact)}
                    </span>
                  </div>
                  <p className="mt-2 text-sm leading-6 text-slate-400">
                    {formatDateTime(selectedE2eRun.finishedAt) || 'No timestamp'} · {selectedE2eRun.environment || 'local'} · {formatDuration(selectedE2eRun.summary?.durationMs)}
                  </p>
                  <p className="mt-4 max-w-3xl text-sm leading-6 text-slate-700">
                    {stripAnsi(selectedE2eRun.plainEnglishSummary) || 'No plain-English summary was saved for this run.'}
                  </p>
                  <p className="mt-3 max-w-4xl text-sm leading-6 text-slate-400">
                    These checks automatically use VegAI on staging like a visitor: product search, manual ingredients, Food Profile, favorites, etc. Photo scan is skipped by default to save cost. Google sign-in is not tested.
                  </p>
                </div>
              ) : (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-teal-700">E2E Testing Results</p>
                  <h2 className="mt-2 text-xl font-bold text-slate-900">No live E2E runs saved yet</h2>
                  <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-400">
                    Run the staging live suite, then refresh this tab. Command will show what worked, what needs a look, and what was skipped.
                  </p>
                  <code className="mt-3 block max-w-xl break-all rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-teal-800">
                    cd frontend && npm run test:e2e:live
                  </code>
                </div>
              )}
            </section>

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <AnalyticsMetric
                label="Things we tested"
                value={selectedE2eRun?.summary?.total ?? 0}
                detail="Click to see all checks"
                active={e2eDetailView === 'all'}
                onClick={() => { setE2eDetailView('all'); setE2eDetailSuite(''); setExpandedE2eCheckId(null); }}
              />
              <AnalyticsMetric
                label="Worked as expected"
                value={selectedE2eRun?.summary?.passed ?? 0}
                detail="Click to see what worked"
                tone="emerald"
                active={e2eDetailView === 'passed'}
                onClick={() => { setE2eDetailView('passed'); setE2eDetailSuite(''); setExpandedE2eCheckId(null); }}
              />
              <AnalyticsMetric
                label="Needs a look"
                value={selectedE2eRun?.summary?.failed ?? 0}
                detail="Click to see what needs review"
                tone="amber"
                active={e2eDetailView === 'failed'}
                onClick={() => { setE2eDetailView('failed'); setE2eDetailSuite(''); setExpandedE2eCheckId(null); }}
              />
              <AnalyticsMetric
                label="Skipped / didn't run"
                value={selectedE2eRun?.summary?.skipped ?? 0}
                detail={
                  selectedE2eRun
                    ? `${selectedE2eSkipBreakdown.intentional} skipped on purpose · ${selectedE2eSkipBreakdown.didNotRun} didn't run`
                    : 'Click to see why'
                }
                tone="sky"
                active={e2eDetailView === 'skipped'}
                onClick={() => { setE2eDetailView('skipped'); setE2eDetailSuite(''); setExpandedE2eCheckId(null); }}
              />
            </div>

            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
              <div className="grid gap-4">
                <WorkCard
                  title={e2eDetailTitle(e2eDetailView, e2eDetailSuite)}
                  actionLabel={e2eDetailView === 'suite' ? 'Hide' : undefined}
                  onAction={
                    e2eDetailView === 'suite'
                      ? () => {
                          setE2eDetailView(defaultE2eDetailViewForRun(selectedE2eRun));
                          setE2eDetailSuite('');
                          setExpandedE2eCheckId(null);
                        }
                      : undefined
                  }
                >
                  {e2eDetailView === 'suite' ? (
                    <div className="sticky top-2 z-10 flex items-center justify-between gap-3 rounded-xl border border-teal-600/25 bg-white/95 px-3 py-2 shadow-lg shadow-black/20 ">
                      <p className="text-xs text-slate-400">You can hide this suite list without scrolling to the bottom.</p>
                      <button
                        type="button"
                        onClick={() => {
                          setE2eDetailView(defaultE2eDetailViewForRun(selectedE2eRun));
                          setE2eDetailSuite('');
                          setExpandedE2eCheckId(null);
                        }}
                        className="shrink-0 text-xs font-bold text-teal-700 hover:text-teal-800"
                      >
                        Hide suite
                      </button>
                    </div>
                  ) : null}
                  {selectedE2eHasCheckDetails ? (
                    selectedE2eChecks.length > 0 ? (
                      selectedE2eChecks.map((check, index) => {
                        const id = check.id || `${check.suite}-${check.title}-${index}`;
                        const expanded = expandedE2eCheckId === id;
                        const technicalDetail = stripAnsi(check.technicalDetail || check.reason || '');
                        return (
                          <div
                            key={id}
                            className={`w-full rounded-2xl border p-4 text-left transition ${
                              check.status === 'failed'
                                ? 'border-red-400/20 bg-red-500/10'
                                : 'border-slate-200 bg-white'
                            }`}
                          >
                            <button
                              type="button"
                              onClick={() => setExpandedE2eCheckId(expanded ? null : id)}
                              className="w-full text-left transition focus:outline-none focus:ring-2 focus:ring-teal-600/30"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">{check.category || e2eSuiteDisplayName(check.suiteGroup || check.suite)}</p>
                                  <p className="mt-1 font-semibold text-slate-900">{e2eCheckLabel(check)}</p>
                                  <p className="mt-2 text-sm leading-5 text-slate-300">{e2eCheckSummary(check)}</p>
                                </div>
                                <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${e2eCheckStatusClass(check.status)}`}>
                                  {e2eCheckStatusLabel(check)}
                                </span>
                              </div>
                              {check.durationMs ? <p className="mt-2 text-xs text-slate-500">{formatDuration(check.durationMs)}</p> : null}
                              <p className="mt-2 text-xs font-semibold text-teal-700/80">
                                {expanded ? 'Hide details' : 'Show details'}
                              </p>
                            </button>
                            {expanded ? (
                              <div className="mt-3 grid gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm leading-5 text-slate-300">
                                <div>
                                  <p className="text-xs font-bold uppercase tracking-wide text-slate-500">What we tried</p>
                                  <p className="mt-1">{stripAnsi(check.tried || `Ran ${e2eCheckLabel(check)}.`)}</p>
                                </div>
                                <div>
                                  <p className="text-xs font-bold uppercase tracking-wide text-slate-500">What we expected</p>
                                  <p className="mt-1">{stripAnsi(check.expected || 'The check should match the expected staging behavior.')}</p>
                                </div>
                                <div>
                                  <p className="text-xs font-bold uppercase tracking-wide text-slate-500">What happened</p>
                                  <p className="mt-1">{stripAnsi(check.happened || check.reason || e2eCheckSummary(check))}</p>
                                </div>
                                {(check.status === 'failed' || check.status === 'skipped') ? (
                                  <div>
                                    <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{check.status === 'failed' ? 'Why it failed' : 'Why it was skipped'}</p>
                                    <p className="mt-1">{e2eCheckWhy(check)}</p>
                                  </div>
                                ) : null}
                                {check.status === 'failed' && check.whatToDoNext ? (
                                  <div>
                                    <p className="text-xs font-bold uppercase tracking-wide text-red-100/70">What to do next</p>
                                    <p className="mt-1">{stripAnsi(check.whatToDoNext)}</p>
                                  </div>
                                ) : null}
                                {isEngineerTechnicalDetail(technicalDetail) ? (
                                  <details
                                    className="border-t border-slate-200 pt-3"
                                    onClick={(event) => event.stopPropagation()}
                                  >
                                    <summary className="cursor-pointer text-xs font-bold uppercase tracking-wide text-slate-500">
                                      Technical detail
                                    </summary>
                                    <p className="mt-2 whitespace-pre-wrap break-words text-xs text-slate-400">{technicalDetail}</p>
                                  </details>
                                ) : null}
                                <button
                                  type="button"
                                  onClick={() => setExpandedE2eCheckId(null)}
                                  className="justify-self-start text-xs font-semibold text-teal-700 hover:text-teal-800"
                                >
                                  Hide details
                                </button>
                              </div>
                            ) : null}
                          </div>
                        );
                      })
                    ) : (
                      <p className="rounded-2xl border border-dashed border-slate-300 p-4 text-sm text-slate-500">
                        {selectedE2eMissingFailureDetails
                          ? 'This run says checks need review, but it did not save the per-check details for this box. Rerun staging E2E after the reporting fix so Command can list each item.'
                          : 'No checks match this box for the selected run.'}
                      </p>
                    )
                  ) : (
                    <p className="rounded-2xl border border-dashed border-slate-300 p-4 text-sm text-slate-500">
                      This run was saved before Command stored per-check details. Run the live E2E suite again to see the exact skipped check and reason here.
                    </p>
                  )}
                </WorkCard>

                <WorkCard title="What we checked">
                  {(selectedE2eRun?.suites || []).length > 0 ? (
                    selectedE2eRun.suites.map((suite) => (
                      <button
                        key={suite.name}
                        type="button"
                        onClick={() => {
                          const alreadySelected = e2eDetailView === 'suite' && e2eDetailSuite === suite.name;
                          setE2eDetailView(alreadySelected ? defaultE2eDetailViewForRun(selectedE2eRun) : 'suite');
                          setE2eDetailSuite(alreadySelected ? '' : suite.name);
                          setExpandedE2eCheckId(null);
                        }}
                        className={`w-full rounded-2xl border p-4 text-left transition hover:border-teal-500/50 focus:outline-none focus:ring-2 focus:ring-teal-600/30 ${
                          e2eDetailView === 'suite' && e2eDetailSuite === suite.name
                            ? 'border-teal-600/40 bg-teal-600/10'
                            : 'border-slate-200 bg-white'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-semibold text-slate-900">{e2eSuiteDisplayName(suite.name)}</p>
                            <p className="mt-1 text-xs text-slate-500">
                              {formatDuration(suite.durationMs)} ·{' '}
                              {e2eDetailView === 'suite' && e2eDetailSuite === suite.name
                                ? 'Selected · click again to hide'
                                : 'Click for check details'}
                            </p>
                          </div>
                          <div className="flex flex-wrap justify-end gap-2 text-xs font-bold">
                            <span className="rounded-full bg-teal-600/10 px-2.5 py-1 text-teal-800">{suite.passed} pass</span>
                            <span className="rounded-full bg-red-400/15 px-2.5 py-1 text-red-700">{suite.failed} fail</span>
                            <span className="rounded-full bg-sky-400/15 px-2.5 py-1 text-sky-800">
                              {suite.didNotRun ? `${suite.didNotRun} didn't run` : `${suite.skipped} skip`}
                            </span>
                          </div>
                        </div>
                      </button>
                    ))
                  ) : (
                    <p className="rounded-2xl border border-dashed border-slate-300 p-4 text-sm text-slate-500">No suite breakdown saved.</p>
                  )}
                </WorkCard>
              </div>

              <WorkCard title="History" actionLabel="Clear all" onAction={clearE2eHistory}>
                <button
                  type="button"
                  onClick={loadE2eResults}
                  className="mb-3 text-xs font-bold text-teal-700 hover:text-teal-800"
                >
                  Refresh
                </button>
                {searchedE2eRuns.length > 0 ? (
                  searchedE2eRuns.map((run) => (
                    <div
                      key={run.id}
                      className={`rounded-2xl border p-4 transition ${
                        selectedE2eRun?.id === run.id
                          ? 'border-teal-600/40 bg-teal-600/10'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <button type="button" onClick={() => selectE2eRun(run)} className="min-w-0 text-left">
                          <p className="font-semibold text-slate-900">Run #{run.id}</p>
                          <p className="mt-1 text-xs text-slate-500">{formatDateTime(run.finishedAt) || 'No timestamp'}</p>
                          <p className="mt-1 text-xs text-slate-500">{run.environment || 'local'} · {formatDuration(run.summary?.durationMs)}</p>
                        </button>
                        <div className="flex shrink-0 items-center gap-2">
                          <span className={`rounded-full border px-2.5 py-1 text-xs font-bold uppercase ${e2eStatusClass(run.status)}`}>
                            {run.status}
                          </span>
                          <button
                            type="button"
                            onClick={() => deleteE2eRun(run)}
                            className="rounded-full border border-red-400/20 bg-red-500/10 p-2 text-red-700 transition hover:border-red-300/50 hover:bg-red-500/20"
                            title={`Delete run #${run.id}`}
                            aria-label={`Delete run ${run.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                      <button type="button" onClick={() => selectE2eRun(run)} className="mt-3 block text-left text-xs text-slate-400">
                        {run.summary?.passed ?? 0}/{run.summary?.total ?? 0} passed · {run.failureCount ?? 0} summarized failures
                        {run.summary?.didNotRun ? ` · ${run.summary.didNotRun} didn't run` : ''}
                      </button>
                    </div>
                  ))
                ) : (
                  <p className="rounded-2xl border border-dashed border-slate-300 p-4 text-sm text-slate-500">No matching E2E runs.</p>
                )}
              </WorkCard>
            </div>

            <details className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-400">
              <summary className="cursor-pointer font-semibold text-slate-700">For developers</summary>
              <code className="mt-3 block break-all rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-teal-800">
                cd frontend && npm run test:e2e:live
              </code>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard?.writeText?.('cd frontend && npm run test:e2e:live');
                  setMessage('Copied the local E2E command.');
                }}
                className="mt-3 text-xs font-bold text-teal-700 hover:text-teal-800"
              >
                Copy command
              </button>
            </details>

          </div>
        ) : adminView === 'learning' ? (
          <LearningArchivePanel secret={secret} search={workspaceSearch} />
        ) : adminView === 'docs' ? (
          <div className="grid gap-4 lg:grid-cols-[1fr_1fr]">
            <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm shadow-slate-900/5 lg:col-span-2">
              <div className="flex items-start gap-3">
                <div className="rounded-2xl border border-teal-600/30 bg-teal-600/10 p-3 text-teal-800">
                  <BookOpen className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Docs / Review Hub</h2>
                  <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-400">
                    Quick links for reviewing the foundation, feature fat files, launch notes, and operating rules while working in Command.
                  </p>
                </div>
              </div>
            </section>
            {DOC_LINKS.map((section) => {
              const visibleLinks = section.links.filter((link) => matchesSearch(link, workspaceSearch));
              if (visibleLinks.length === 0) return null;
              return (
                <WorkCard key={section.group} title={section.group}>
                  {visibleLinks.map((link) => (
                    <DocLinkCard key={link.href} {...link} />
                  ))}
                </WorkCard>
              );
            })}
          </div>
        ) : (adminView === 'queue' || adminView === 'failures') ? (
        <div className="grid gap-4 lg:grid-cols-[360px_1fr]">
          <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm shadow-slate-900/5">
            <div className="mb-3 flex items-center justify-between gap-3">
              <h2 className="text-lg font-bold text-slate-900">{adminView === 'failures' ? 'Backend Errors' : 'Open Queue'}</h2>
              <span className="rounded-full bg-slate-800 px-2.5 py-1 text-xs font-semibold text-slate-300">
                {adminView === 'failures' ? `${failureItems.length + searchedOpsEvents.length}/${allFailureItems.length + opsEvents.length}` : `${filteredItems.length}/${reviewItems.length}`}
              </span>
            </div>
            <div className="mb-4 flex flex-wrap gap-2">
              {QUEUE_STATUS_FILTERS.map((filter) => (
                <button
                  key={filter.id}
                  type="button"
                  onClick={async () => {
                    setQueueStatusFilter(filter.id);
                    await loadQueue(filter.id);
                  }}
                  className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                    queueStatusFilter === filter.id
                      ? 'border-sky-400/40 bg-sky-400/15 text-sky-800'
                      : 'border-slate-200 bg-slate-100/70 text-slate-400 hover:border-slate-300 hover:text-slate-700'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
            <div className="mb-4 flex flex-wrap gap-2">
              {(adminView === 'failures' ? FAILURE_FILTERS : FILTERS).map((filter) => (
                <button
                  key={filter.id}
                  type="button"
                  onClick={() => (adminView === 'failures' ? setFailureFilter(filter.id) : setQueueFilter(filter.id))}
                  className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                    (adminView === 'failures' ? failureFilter : queueFilter) === filter.id
                      ? 'border-teal-600/40 bg-teal-600/10 text-teal-800'
                      : 'border-slate-200 bg-slate-100/70 text-slate-400 hover:border-slate-300 hover:text-slate-700'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
            {adminView === 'queue' ? (
              <label className="mb-4 block space-y-1 text-xs font-semibold text-slate-400">
                Flag category
                <select
                  value={queueFilter.startsWith('category:') ? queueFilter : ''}
                  onChange={(event) => setQueueFilter(event.target.value || 'all')}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700 outline-none focus:border-teal-600/60"
                >
                  <option value="">All categories</option>
                  {FLAG_CATEGORIES.map((category) => (
                    <option key={category.id} value={`category:${category.id}`}>{category.label}</option>
                  ))}
                </select>
              </label>
            ) : null}
            {adminView === 'failures' && searchedOpsEvents.length > 0 && (
              <div className="mb-4 space-y-3">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">System events</p>
                {searchedOpsEvents.map((item) => (
                  <div key={`ops-${item.id}`} className="rounded-2xl border border-amber-400/25 bg-amber-400/10 p-4 text-left">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-amber-900">{item.code || item.category}</p>
                        <p className="mt-1 text-sm leading-5 text-amber-900/80">{item.message}</p>
                        <p className="mt-2 text-xs text-amber-900/60">
                          {[item.method, item.route, formatDateTime(item.updatedAt || item.createdAt)].filter(Boolean).join(' · ')}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={(event) => dismissOpsEvent(item, event)}
                        className="shrink-0 rounded-lg border border-amber-200/30 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-amber-900 transition hover:bg-slate-100"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {(adminView === 'failures' ? failureItems.length + searchedOpsEvents.length : filteredItems.length) === 0 ? (
              <p className="text-sm text-slate-500">No items match this filter.</p>
            ) : (
              <div className="space-y-3">
                {(adminView === 'failures' ? failureItems : filteredItems).map((item) => (
                  <div
                    key={item.id}
                    className={`w-full rounded-2xl border p-4 text-left transition ${
                      selected?.id === item.id
                        ? 'border-teal-600 bg-teal-600/10 shadow-[0_0_0_1px_rgba(52,211,153,0.18)]'
                        : 'border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-slate-100/70'
                    }`}
                  >
                    <button type="button" onClick={() => selectItem(item)} className="block w-full text-left">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-semibold text-slate-900">{reviewItemTitle(item)}</p>
                          {item.reason === 'user_flag' ? (
                            <span className="rounded-full border border-amber-400/30 bg-amber-400/10 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-amber-200">
                              User flag received
                            </span>
                          ) : null}
                        </div>
                        <div className="mt-2 space-y-1 text-xs text-slate-400">
                          <p>Submitted: {formatDateTime(item.createdAt) || 'unknown'}</p>
                          {item.updatedAt ? <p>Updated: {formatDateTime(item.updatedAt)}</p> : null}
                          <p>Reason: {item.reason}</p>
                          {userFlagSummary(item) ? <p>User note: {userFlagSummary(item)}</p> : null}
                          {item.flagCategory ? <p>Category: {flagCategoryLabel(item.flagCategory)}</p> : null}
                          <p>
                            Result: <span className="text-teal-700">{item.lastResult?.status || 'unknown'}</span>
                          </p>
                          <p>
                            Uncertain: {item.uncertainCount} <span className="mx-1 text-teal-700">•</span> Flags: {item.flagCount}
                          </p>
                          {item.adminNotes ? <p className="text-sky-300">Notes saved</p> : null}
                        </div>
                        </div>
                        <span className="mt-1 text-slate-500">›</span>
                      </div>
                    </button>
                    <div className="mt-3 flex justify-end">
                      <button
                        type="button"
                        onClick={(event) => dismissQueueItem(item, event)}
                        className="rounded-lg border border-slate-300 bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-300 transition hover:bg-slate-200"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div id="command-queue-detail" className="admin-dark-surface scroll-mt-6 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm shadow-slate-900/5 sm:p-6">
            {!selected && !manualMode ? (
              <div className="flex min-h-[18rem] flex-col justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-slate-400">
                <p className="max-w-lg text-sm leading-6">
                  {adminView === 'failures'
                    ? 'Select a failure to inspect what happened, or clear it from the list if it is not useful.'
                    : 'Select a queue card to open its verification notes here, or create a verified product manually.'}
                </p>
                <button type="button" onClick={startManualProduct} className="mt-4 inline-flex w-fit items-center gap-2 rounded-xl border border-slate-300 bg-slate-100 px-4 py-2 font-semibold text-slate-900 transition hover:bg-slate-200">
                  <Plus className="h-4 w-4" />
                  New Verified Product
                </button>
              </div>
            ) : adminView === 'failures' && selected ? (
              <div className="space-y-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold uppercase tracking-[0.18em] text-amber-300">Incident Details</p>
                    <h2 className="mt-2 text-2xl font-bold text-slate-900">{failureSummary(selected)?.productMatch || failureSummary(selected)?.query || 'Backend error'}</h2>
                    <p className="mt-2 text-sm leading-6 text-slate-400">
                      Use this view to understand what failed, what VegAI tried, and whether this needs a verified product, source fix, or code follow-up.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={closeEditor}
                    className="shrink-0 rounded-xl p-2 text-slate-500 transition hover:bg-slate-200 hover:text-slate-800"
                    aria-label="Close incident"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="grid gap-3 md:grid-cols-4">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Reason</p>
                    <p className="mt-1 break-words text-sm font-semibold text-slate-900">{failureSummary(selected)?.reason || 'unknown'}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Backend Status</p>
                    <p className="mt-1 break-words text-sm font-semibold text-slate-900">{failureSummary(selected)?.status || 'unknown'}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Resolver</p>
                    <p className="mt-1 break-words text-sm font-semibold text-slate-900">{failureSummary(selected)?.resolvedVia || 'unknown'}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Submitted</p>
                    <p className="mt-1 break-words text-sm font-semibold text-slate-900">{formatDateTime(failureSummary(selected)?.createdAt) || 'unknown'}</p>
                    {failureSummary(selected)?.updatedAt ? (
                      <p className="mt-1 text-xs text-slate-500">Updated {formatDateTime(failureSummary(selected)?.updatedAt)}</p>
                    ) : null}
                  </div>
                </div>

                <div className="rounded-2xl border border-teal-600/20 bg-teal-600/10 p-4">
                  <p className="text-sm font-bold text-teal-900">Plain-English Summary</p>
                  <p className="mt-2 text-sm leading-6 text-emerald-50/90">{plainEnglishFailure(failureSummary(selected))}</p>
                </div>

                <label className="block space-y-2 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm font-semibold text-slate-900">
                  Investigation Notes
                  <textarea
                    className="h-28 w-full rounded-xl border border-slate-300 bg-slate-100 px-3 py-2 text-sm font-normal text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-teal-600/60"
                    value={reviewNotes}
                    onChange={(event) => setReviewNotes(event.target.value)}
                    placeholder="What happened, what you checked, and what should happen next..."
                  />
                  <span className="block text-xs font-normal text-slate-500">
                    These notes stay with this Command record so future reviews do not depend on chat memory.
                  </span>
                </label>

                <div className="grid gap-4 lg:grid-cols-2">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="mb-3 text-sm font-bold text-slate-900">User Input</p>
                    <dl className="space-y-2 text-sm text-slate-300">
                      <div className="grid gap-1 sm:grid-cols-[120px_1fr]">
                        <dt className="font-semibold text-slate-500">Query</dt>
                        <dd className="break-words">{failureSummary(selected)?.query || 'Not saved'}</dd>
                      </div>
                      <div className="grid gap-1 sm:grid-cols-[120px_1fr]">
                        <dt className="font-semibold text-slate-500">Match</dt>
                        <dd className="break-words">{failureSummary(selected)?.productMatch || 'No product match saved'}</dd>
                      </div>
                      <div className="grid gap-1 sm:grid-cols-[120px_1fr]">
                        <dt className="font-semibold text-slate-500">User Saw</dt>
                        <dd className="break-words">{failureSummary(selected)?.message || 'No user-facing message saved.'}</dd>
                      </div>
                    </dl>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="mb-3 text-sm font-bold text-slate-900">Source Evidence</p>
                    <dl className="space-y-2 text-sm text-slate-300">
                      <div className="grid gap-1 sm:grid-cols-[140px_1fr]">
                        <dt className="font-semibold text-slate-500">Source types</dt>
                        <dd>{failureSummary(selected)?.sourceTypes?.join(', ') || 'None saved'}</dd>
                      </div>
                      <div className="grid gap-1 sm:grid-cols-[140px_1fr]">
                        <dt className="font-semibold text-slate-500">Trusted count</dt>
                        <dd>{failureSummary(selected)?.trustedSourceCount ?? 'Unknown'}</dd>
                      </div>
                      <div className="grid gap-1 sm:grid-cols-[140px_1fr]">
                        <dt className="font-semibold text-slate-500">Best source</dt>
                        <dd>{failureSummary(selected)?.bestSourceType || 'Unknown'} {failureSummary(selected)?.bestTrustScore ? `(${failureSummary(selected).bestTrustScore})` : ''}</dd>
                      </div>
                      <div className="grid gap-1 sm:grid-cols-[140px_1fr]">
                        <dt className="font-semibold text-slate-500">Warnings</dt>
                        <dd>{failureSummary(selected)?.warnings?.join(', ') || 'None saved'}</dd>
                      </div>
                    </dl>
                  </div>
                </div>

                {failureSummary(selected)?.sources?.length > 0 && (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="mb-3 text-sm font-bold text-slate-900">Sources VegAI Returned</p>
                    <ul className="space-y-2 text-sm text-slate-300">
                      {failureSummary(selected).sources.map((source, index) => (
                        <li key={`${source.url || source.title}-${index}`} className="break-words">
                          <a className="text-teal-700 underline decoration-emerald-300/40 underline-offset-4" href={source.url} target="_blank" rel="noreferrer">
                            {source.title || source.name || source.domain || source.url || 'Source'}
                          </a>
                          {source.label ? <span className="ml-2 text-slate-500">({source.label})</span> : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="grid gap-4 lg:grid-cols-2">
                  <details className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-300" open>
                    <summary className="cursor-pointer font-bold text-slate-900">Rejected source details</summary>
                    <pre className="mt-3 max-h-72 overflow-auto whitespace-pre-wrap break-words rounded-xl bg-slate-100 p-3 text-xs text-slate-300">
                      {failureSummary(selected)?.rejectedSources?.length ? conciseJson(failureSummary(selected).rejectedSources) : 'No rejected source details saved.'}
                    </pre>
                  </details>
                  <details className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-300">
                    <summary className="cursor-pointer font-bold text-slate-900">Raw backend result</summary>
                    <pre className="mt-3 max-h-72 overflow-auto whitespace-pre-wrap break-words rounded-xl bg-slate-100 p-3 text-xs text-slate-300">
                      {conciseJson(failureSummary(selected)?.rawResult)}
                    </pre>
                  </details>
                </div>

                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={saveReviewNotes}
                    className="rounded-xl border border-slate-300 bg-slate-100 px-4 py-2 font-semibold text-slate-700 transition hover:bg-slate-200"
                  >
                    Save Notes
                  </button>
                  <button
                    type="button"
                    onClick={() => changeReviewItemStatus(selected, 'reviewing', 'Marked investigating')}
                    className="rounded-xl border border-sky-400/40 bg-sky-500/15 px-4 py-2 font-semibold text-sky-800 transition hover:bg-sky-500/25"
                  >
                    Mark Investigating
                  </button>
                  <button
                    type="button"
                    onClick={() => changeReviewItemStatus(selected, 'resolved', 'Marked resolved')}
                    className="rounded-xl border border-teal-600/40 bg-teal-600/10 px-4 py-2 font-semibold text-teal-800 transition hover:bg-teal-600/15"
                  >
                    Mark Resolved
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      window.navigator?.clipboard?.writeText(conciseJson(failureSummary(selected)?.rawResult));
                      setMessage('Copied backend details.');
                    }}
                    className="rounded-xl border border-slate-300 bg-slate-100 px-4 py-2 font-semibold text-slate-700 transition hover:bg-slate-200"
                  >
                    Copy Debug Details
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setAdminView('queue');
                      setManualMode(true);
                      setForm(prefillFromItem(selected));
                    }}
                    className="rounded-xl border border-teal-600/40 bg-teal-600/10 px-4 py-2 font-semibold text-teal-800 transition hover:bg-teal-600/15"
                  >
                    Create Verified Product
                  </button>
                  <button type="button" onClick={dismissSelected} className="rounded-xl border border-slate-300 bg-slate-100 px-4 py-2 font-semibold text-slate-700 transition hover:bg-slate-200">
                    Clear Error
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <h2 className="text-2xl font-bold text-slate-900">{selected ? reviewItemTitle(selected) : 'New Verified Product'}</h2>
                    {selected && (
                      <>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <span className="rounded-full border border-teal-600/30 bg-teal-600/10 px-3 py-1 text-xs font-semibold text-teal-800">
                            Result: {selected.lastResult?.status || 'unknown'}
                          </span>
                          <span className="rounded-full border border-slate-300 bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-300">
                            Reason: {selected.reason}
                          </span>
                          {selected.reason === 'user_flag' ? (
                            <span className="rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1 text-xs font-semibold text-amber-200">
                              User flag received
                            </span>
                          ) : null}
                          {userFlagSummary(selected) ? (
                            <span className="rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1 text-xs font-semibold text-amber-900">
                              {userFlagSummary(selected)}
                            </span>
                          ) : null}
                          <span className="rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1 text-xs font-semibold text-amber-200">
                            Flags: {selected.flagCount}
                          </span>
                        </div>
                        <p className="mt-2 text-sm text-slate-500">
                          Submitted {formatDateTime(selected.createdAt) || 'unknown'}
                          {selected.updatedAt ? ` · Updated ${formatDateTime(selected.updatedAt)}` : ''}
                        </p>
                      </>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={closeEditor}
                    className="shrink-0 rounded-xl p-2 text-slate-500 transition hover:bg-slate-200 hover:text-slate-800"
                    aria-label="Close editor"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                {selected?.reason === 'user_flag' ? (
                  <div className="rounded-2xl border border-sky-400/25 bg-sky-400/10 p-4">
                    <label className="block space-y-2 text-sm font-semibold text-sky-900">
                      Admin flag category
                      <div className="flex flex-col gap-2 sm:flex-row">
                        <select
                          value={reviewFlagCategory}
                          onChange={(event) => setReviewFlagCategory(event.target.value)}
                          className="min-w-0 flex-1 rounded-xl border border-sky-300/25 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-sky-300/60"
                        >
                          <option value="">Choose a category</option>
                          {FLAG_CATEGORIES.map((category) => (
                            <option key={category.id} value={category.id}>{category.label}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={saveReviewFlagCategory}
                          disabled={!reviewFlagCategory}
                          className="rounded-xl bg-sky-300 px-4 py-2.5 font-semibold text-slate-950 transition hover:bg-sky-200 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Save category
                        </button>
                      </div>
                      <span className="block text-xs font-normal leading-5 text-sky-900/70">
                        VegAI starts with the reason the user chose. Change it here when a different category is more useful for future review.
                      </span>
                    </label>
                  </div>
                ) : null}

                <div className="grid gap-4 md:grid-cols-2">
                  <label className="space-y-1 text-sm font-semibold">
                    Product Name
                    <input className="w-full rounded-xl border px-3 py-2" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  </label>
                  <label className="space-y-1 text-sm font-semibold">
                    Brand
                    <input className="w-full rounded-xl border px-3 py-2" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} />
                  </label>
                </div>

                {selected && failureSummary(selected) && (
                  <div className="rounded-2xl border border-amber-400/25 bg-amber-400/10 p-4">
                    <p className="text-sm font-bold text-amber-900">Lookup failure report</p>
                    <div className="mt-3 grid gap-3 text-sm text-amber-50/90 md:grid-cols-3">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-amber-200/80">Error</p>
                        <p className="break-words">{failureSummary(selected).error}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-amber-200/80">Resolver</p>
                        <p>{failureSummary(selected).resolvedVia}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-amber-200/80">Warnings</p>
                        <p>{failureSummary(selected).warnings.join(', ') || 'None saved'}</p>
                      </div>
                    </div>
                    <p className="mt-3 text-sm leading-6 text-amber-50/90">{failureSummary(selected).message}</p>
                    {failureSummary(selected).rejectedSources.length > 0 && (
                      <details className="mt-3 rounded-xl border border-amber-300/20 bg-slate-50 p-3 text-xs text-amber-50/90">
                        <summary className="cursor-pointer font-semibold text-amber-900">Rejected source details</summary>
                        <pre className="mt-3 max-h-56 overflow-auto whitespace-pre-wrap break-words">{conciseJson(failureSummary(selected).rejectedSources)}</pre>
                      </details>
                    )}
                  </div>
                )}

                <label className="block space-y-1 text-sm font-semibold">
                  Aliases
                  <textarea className="h-20 w-full rounded-xl border px-3 py-2" value={form.aliases} onChange={(e) => setForm({ ...form, aliases: e.target.value })} />
                </label>

                <div className="grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
                  <label className="block space-y-1 text-sm font-semibold">
                    Ingredient Evidence / Notes
                    <textarea className="h-40 w-full rounded-xl border px-3 py-2 font-mono text-sm" value={form.ingredients} onChange={(e) => setForm({ ...form, ingredients: e.target.value })} />
                  </label>
                  <label className="block space-y-1 text-sm font-semibold">
                    Reviewer Notes
                    <textarea
                      className="h-40 w-full rounded-xl border px-3 py-2"
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      placeholder="Add concise review notes..."
                    />
                  </label>
                </div>

                <div className="grid gap-4 md:grid-cols-3">
                  <label className="space-y-1 text-sm font-semibold">
                    Verified Status
                    <select className="w-full rounded-xl border px-3 py-2" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                      <option value="vegan">Vegan</option>
                      <option value="not-vegan">Not Vegan</option>
                      <option value="maybe-vegan">Maybe Vegan</option>
                    </select>
                  </label>
                  <label className="space-y-1 text-sm font-semibold">
                    Source Type
                    <input className="w-full rounded-xl border px-3 py-2" value={form.sourceType} onChange={(e) => setForm({ ...form, sourceType: e.target.value })} />
                  </label>
                  <RecheckByField
                    value={form.expiresAt}
                    onChange={(event) => {
                      setForm({ ...form, expiresAt: event.target.value });
                      setRecheckHint('');
                    }}
                    onExtend={extendRecheckBy}
                    menuOpen={recheckExtendOpen}
                    onToggleMenu={() => setRecheckExtendOpen((open) => !open)}
                    hint={recheckHint}
                    optional
                    showDefaultHelp
                  />
                </div>

                <label className="block space-y-1 text-sm font-semibold">
                  Source URL
                  <input className="w-full rounded-xl border px-3 py-2" value={form.sourceUrl} onChange={(e) => setForm({ ...form, sourceUrl: e.target.value })} />
                </label>

                <label className="block space-y-1 text-sm font-semibold">
                  Verification reason
                  <span className="font-normal text-red-600"> *</span>
                  <textarea
                    className="mt-1 h-24 w-full rounded-xl border px-3 py-2"
                    value={form.verificationReason}
                    onChange={(e) => setForm({ ...form, verificationReason: e.target.value })}
                    placeholder="Why did you choose this verified status? Use N/A only if no reason applies."
                  />
                  <span className="block text-xs font-normal text-slate-500">
                    Required. Enter a short explanation, or <strong className="font-semibold text-slate-600">N/A</strong> (any spacing/case) if there is no specific reason.
                    Whitespace-only or symbol-only text is not accepted.
                  </span>
                </label>

                <div className="grid gap-4 md:grid-cols-1">
                  <label className="space-y-1 text-sm font-semibold">
                    Verified By
                    <input className="w-full rounded-xl border px-3 py-2" value={form.verifiedBy} onChange={(e) => setForm({ ...form, verifiedBy: e.target.value })} />
                  </label>
                </div>

                {selectedSources.length > 0 && (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="mb-2 text-sm font-bold text-slate-900">Sources from lookup</p>
                    <ul className="space-y-1 text-sm text-slate-300">
                      {selectedSources.map((source, index) => (
                        <li key={`${source.url || source.title}-${index}`}>
                          <a className="text-teal-700 underline decoration-emerald-300/40 underline-offset-4" href={source.url} target="_blank" rel="noreferrer">
                            {source.title || source.domain || source.url}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex flex-wrap gap-3">
                  {(manualMode || selected) && (
                    <button
                      type="button"
                      onClick={closeEditor}
                    className="rounded-xl border border-slate-300 bg-slate-100 px-4 py-2 font-semibold text-slate-700 transition hover:bg-slate-200"
                    >
                      Close
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={saveVerified}
                    disabled={!isValidVerificationReason(form.verificationReason)}
                    className="inline-flex items-center gap-2 rounded-xl bg-teal-700 px-4 py-2 font-semibold text-white transition hover:bg-teal-600 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <CheckCircle className="h-4 w-4" />
                    Save Verified Product
                  </button>
                  {selected && (
                    <button type="button" onClick={dismissSelected} className="rounded-xl border border-slate-300 bg-slate-100 px-4 py-2 font-semibold text-slate-700 transition hover:bg-slate-200">
                      Dismiss
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        ) : adminView === 'feedback' ? (
          <div className="grid gap-4 lg:grid-cols-[380px_1fr]">
            <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm shadow-slate-900/5">
              <div className="mb-3 flex items-center justify-between gap-3">
                <h2 className="text-lg font-bold text-slate-900">Feedback Inbox</h2>
                <span className="rounded-full bg-slate-800 px-2.5 py-1 text-xs font-semibold text-slate-300">
                  {feedbackItems.length}
                </span>
              </div>
              <div className="mb-4 flex flex-wrap gap-2">
                {FEEDBACK_FILTERS.map((filter) => (
                  <button
                    key={filter.id}
                    type="button"
                    onClick={async () => {
                      setFeedbackFilter(filter.id);
                      await loadFeedbackInbox(filter.id);
                    }}
                    className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                      feedbackFilter === filter.id
                        ? 'border-teal-600/40 bg-teal-600/10 text-teal-800'
                        : 'border-slate-200 bg-slate-100/70 text-slate-400 hover:border-slate-300 hover:text-slate-700'
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
              {searchedFeedbackItems.length === 0 ? (
                <p className="text-sm text-slate-500">No feedback items for this filter.</p>
              ) : (
                <div className="space-y-3">
                  {searchedFeedbackItems.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => selectFeedback(item)}
                      className={`w-full rounded-2xl border p-4 text-left transition ${
                        selectedFeedback?.id === item.id
                          ? 'border-teal-600 bg-teal-600/10 shadow-[0_0_0_1px_rgba(52,211,153,0.18)]'
                          : 'border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-slate-100/70'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-semibold capitalize text-slate-900">{item.category}</p>
                          <p className="mt-1 text-sm text-slate-400">{item.message}</p>
                          <p className="mt-2 text-xs text-slate-500">
                            Submitted {formatDateTime(item.createdAt) || 'unknown'} · {item.status}
                            {item.updatedAt ? ` · Updated ${formatDateTime(item.updatedAt)}` : ''}
                          </p>
                        </div>
                        <span className="mt-1 text-slate-500">›</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div id="command-feedback-detail" className="admin-dark-surface scroll-mt-6 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm shadow-slate-900/5 sm:p-6">
              {!selectedFeedback ? (
                <div className="flex min-h-[18rem] flex-col justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-slate-400">
                  <p className="max-w-lg text-sm leading-6">
                    Select a feedback item to review the user message, context, correction details, and next action.
                  </p>
                </div>
              ) : (
                <div className="space-y-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-bold uppercase tracking-[0.18em] text-teal-700">
                        Feedback #{selectedFeedback.id}
                      </p>
                      <h2 className="mt-2 text-2xl font-bold capitalize text-slate-900">{selectedFeedback.category}</h2>
                      <p className="mt-1 text-sm text-slate-500">
                        Created {formatDateTime(selectedFeedback.createdAt) || 'unknown'} · Status {selectedFeedback.status}
                        {selectedFeedback.updatedAt ? ` · Updated ${formatDateTime(selectedFeedback.updatedAt)}` : ''}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={closeEditor}
                      className="shrink-0 rounded-xl p-2 text-slate-500 transition hover:bg-slate-200 hover:text-slate-800"
                      aria-label="Close feedback"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="mb-2 text-sm font-bold text-slate-900">User message</p>
                    <p className="whitespace-pre-wrap text-sm leading-6 text-slate-300">{selectedFeedback.message}</p>
                  </div>

                  {selectedFeedback.contactEmail ? (
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="mb-2 text-sm font-bold text-slate-900">Contact</p>
                      <p className="break-words text-sm text-slate-300">{selectedFeedback.contactEmail}</p>
                    </div>
                  ) : null}

                  {selectedFeedback.context ? (
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="mb-2 text-sm font-bold text-slate-900">Context</p>
                      <dl className="space-y-1 text-sm text-slate-300">
                        {Object.entries(selectedFeedback.context).map(([key, value]) => (
                          <div key={key} className="grid gap-1 sm:grid-cols-[130px_1fr]">
                            <dt className="font-semibold text-slate-500">{key}</dt>
                            <dd className="break-words">{String(value)}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  ) : null}

                  {selectedFeedback.correction || selectedFeedback.correctionResult ? (
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="mb-2 text-sm font-bold text-slate-900">Correction</p>
                      {selectedFeedback.correction ? (
                        <pre className="overflow-auto rounded-xl bg-slate-100 p-3 text-xs text-slate-300">
                          {JSON.stringify(selectedFeedback.correction, null, 2)}
                        </pre>
                      ) : null}
                      {selectedFeedback.correctionResult ? (
                        <p className="mt-2 text-sm text-slate-300">
                          Result: {selectedFeedback.correctionResult.applied ? 'applied' : selectedFeedback.correctionResult.reason}
                        </p>
                      ) : null}
                      {selectedFeedback.correction && selectedFeedback.correctionResult?.reason === 'pending-admin-review' ? (
                        <p className="mt-2 text-sm text-amber-200">
                          This correction is waiting for admin approval before it changes VegAI rules.
                        </p>
                      ) : null}
                    </div>
                  ) : null}

                  <label className="block space-y-1 text-sm font-semibold">
                    Admin Notes
                    <textarea
                      className="h-28 w-full rounded-xl border px-3 py-2"
                      value={feedbackNotes}
                      onChange={(e) => setFeedbackNotes(e.target.value)}
                      placeholder="What did you do with this feedback?"
                    />
                  </label>

                  <div className="flex flex-wrap gap-3">
                    {selectedFeedback.correction ? (
                      <button type="button" onClick={applySelectedFeedbackCorrection} className="rounded-xl bg-amber-400 px-4 py-2 font-semibold text-slate-950 transition hover:bg-amber-300">
                        Apply Correction
                      </button>
                    ) : null}
                    <button type="button" onClick={() => changeFeedbackStatus('reviewing')} className="rounded-xl border border-slate-300 bg-slate-100 px-4 py-2 font-semibold text-slate-700 transition hover:bg-slate-200">
                      Mark Reviewing
                    </button>
                    <button type="button" onClick={() => changeFeedbackStatus('resolved')} className="rounded-xl bg-teal-700 px-4 py-2 font-semibold text-white transition hover:bg-teal-600">
                      Resolve
                    </button>
                    <button type="button" onClick={() => changeFeedbackStatus('dismissed')} className="rounded-xl border border-slate-300 bg-slate-100 px-4 py-2 font-semibold text-slate-700 transition hover:bg-slate-200">
                      Dismiss
                    </button>
                    <button type="button" onClick={() => changeFeedbackStatus('open')} className="rounded-xl border border-slate-300 bg-slate-100 px-4 py-2 font-semibold text-slate-700 transition hover:bg-slate-200">
                      Reopen
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm shadow-slate-900/5">
              <h2 className="mb-2 text-lg font-bold text-slate-900">Verified products</h2>
              <p className="mb-4 text-sm text-slate-400">
                Click a saved verification to open its notes and editable details below the catalog.
              </p>
              <div className="mb-4 grid gap-3 sm:grid-cols-4">
                <div className="rounded-2xl border border-slate-200 bg-white p-3">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Total</p>
                  <p className="mt-1 text-2xl font-bold text-slate-900">{verifiedCatalogCounts.total}</p>
                </div>
                <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-3">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-800">Needs review</p>
                  <p className="mt-1 text-2xl font-bold text-amber-950">{verifiedCatalogCounts.needsReview}</p>
                </div>
                <div className="rounded-2xl border border-yellow-500/30 bg-yellow-500/10 p-3">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-yellow-800">Expiring soon</p>
                  <p className="mt-1 text-2xl font-bold text-yellow-950">{verifiedCatalogCounts.expiringSoon}</p>
                </div>
                <div className="rounded-2xl border border-emerald-500/30 bg-teal-700/10 p-3">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-teal-800/70">Current</p>
                  <p className="mt-1 text-2xl font-bold text-teal-900">{verifiedCatalogCounts.current}</p>
                </div>
              </div>
              <div className="mb-4 flex flex-wrap gap-2">
                {VERIFIED_REVIEW_FILTERS.map((filter) => (
                  <button
                    key={filter.id}
                    type="button"
                    onClick={() => setVerifiedReviewFilter(filter.id)}
                    className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                      verifiedReviewFilter === filter.id
                        ? 'border-teal-600 bg-teal-600/10 text-teal-900'
                        : 'border-slate-200 bg-slate-50 text-slate-400 hover:border-slate-300 hover:text-slate-700'
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
              {searchedVerifiedRows.length === 0 ? (
                <p className="text-sm text-slate-500">No verified records loaded. Use Refresh catalog.</p>
              ) : (
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {searchedVerifiedRows.map((row) => {
                    const badge = verifiedReviewBadge(row);
                    return (
                      <button
                        key={row.id}
                        type="button"
                        onClick={() => selectVerifiedRow(row)}
                        className={`w-full rounded-2xl border p-4 text-left transition ${
                          editingVerified?.id === row.id
                            ? 'border-teal-600 bg-teal-600/10 shadow-[0_0_0_1px_rgba(52,211,153,0.18)]'
                            : 'border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-slate-100/70'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-semibold text-slate-900">{row.name}</p>
                            {row.brand ? <p className="text-sm text-slate-400">{row.brand}</p> : null}
                          </div>
                          <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${badge.className}`}>
                            {badge.label}
                          </span>
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-400">
                          <span>
                            Result: <strong className="text-teal-700">{row.status}</strong>
                          </span>
                          {Number.isFinite(Number(row.daysUntilExpiry)) ? (
                            <span>{Number(row.daysUntilExpiry) < 0 ? `${Math.abs(Number(row.daysUntilExpiry))} days overdue` : `${Number(row.daysUntilExpiry)} days left`}</span>
                          ) : (
                            <span>No recheck date</span>
                          )}
                        </div>
                        <p className="mt-1 text-xs text-slate-500">
                          Updated {formatDate(row.updatedAt || row.verifiedAt)}
                          {row.expiresAt ? ` · Recheck by ${formatDate(row.expiresAt)}` : ''}
                        </p>
                        {Array.isArray(row.aliases) && row.aliases.length > 0 ? (
                          <p className="mt-2 break-words text-xs text-slate-500">
                            <span className="font-semibold text-slate-600">Aliases: </span>
                            {row.aliases.join(' · ')}
                          </p>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {editingVerified && (
              <div id="command-verified-editor" className="admin-dark-surface scroll-mt-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm shadow-slate-900/5">
                <div className="space-y-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h2 className="text-2xl font-bold text-slate-900">{editingVerified.name}</h2>
                      <p className="text-sm text-slate-500">Verified catalog record #{editingVerified.id}</p>
                      {String(editingVerified.id || '').startsWith('seed-') ? (
                        <p className="mt-2 text-sm font-medium text-amber-700">
                          Saving will replace this seed record with your saved copy.
                        </p>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      onClick={closeEditor}
                      className="shrink-0 rounded-xl p-2 text-slate-500 transition hover:bg-slate-200 hover:text-slate-800"
                      aria-label="Close editor"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <label className="space-y-1 text-sm font-semibold">
                      Product Name
                      <input className="w-full rounded-xl border px-3 py-2" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                    </label>
                    <label className="space-y-1 text-sm font-semibold">
                      Brand
                      <input className="w-full rounded-xl border px-3 py-2" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} />
                    </label>
                  </div>

                  <label className="block space-y-1 text-sm font-semibold">
                    Aliases
                    <textarea className="h-24 w-full rounded-xl border px-3 py-2" value={form.aliases} onChange={(e) => setForm({ ...form, aliases: e.target.value })} />
                  </label>

                  <label className="block space-y-1 text-sm font-semibold">
                    Ingredients
                    <textarea className="h-40 w-full rounded-xl border px-3 py-2 font-mono text-sm" value={form.ingredients} onChange={(e) => setForm({ ...form, ingredients: e.target.value })} />
                  </label>

                  <div className="grid gap-4 md:grid-cols-3">
                    <label className="space-y-1 text-sm font-semibold">
                      Verified Status
                      <select className="w-full rounded-xl border px-3 py-2" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                        <option value="vegan">Vegan</option>
                        <option value="not-vegan">Not Vegan</option>
                        <option value="maybe-vegan">Maybe Vegan</option>
                      </select>
                    </label>
                    <label className="space-y-1 text-sm font-semibold">
                      Source Type
                      <input className="w-full rounded-xl border px-3 py-2" value={form.sourceType} onChange={(e) => setForm({ ...form, sourceType: e.target.value })} />
                    </label>
                    <RecheckByField
                      value={form.expiresAt}
                      onChange={(event) => {
                        setForm({ ...form, expiresAt: event.target.value });
                        setRecheckHint('');
                      }}
                      onExtend={extendRecheckBy}
                      menuOpen={recheckExtendOpen}
                      onToggleMenu={() => setRecheckExtendOpen((open) => !open)}
                      hint={recheckHint}
                    />
                  </div>

                  <label className="block space-y-1 text-sm font-semibold">
                    Source URL
                    <input className="w-full rounded-xl border px-3 py-2" value={form.sourceUrl} onChange={(e) => setForm({ ...form, sourceUrl: e.target.value })} />
                  </label>

                  <label className="block space-y-1 text-sm font-semibold">
                    Verification reason
                    <span className="font-normal text-red-600"> *</span>
                    <textarea
                      className="mt-1 h-20 w-full rounded-xl border px-3 py-2"
                      value={form.verificationReason}
                      onChange={(e) => setForm({ ...form, verificationReason: e.target.value })}
                    />
                  </label>

                  <div className="grid gap-4 md:grid-cols-2">
                    <label className="space-y-1 text-sm font-semibold">
                      Verified By
                      <input className="w-full rounded-xl border px-3 py-2" value={form.verifiedBy} onChange={(e) => setForm({ ...form, verifiedBy: e.target.value })} />
                    </label>
                    <label className="space-y-1 text-sm font-semibold">
                      Notes
                      <textarea className="h-20 w-full rounded-xl border px-3 py-2" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                    </label>
                  </div>

                  {selectedSources.length > 0 && (
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="mb-2 text-sm font-bold text-slate-900">Saved sources</p>
                      <ul className="space-y-1 text-sm text-slate-300">
                        {selectedSources.map((source, index) => (
                          <li key={`${source.url || source.title}-${index}`}>
                            {source.url ? (
                              <a className="text-teal-700 underline decoration-emerald-300/40 underline-offset-4" href={source.url} target="_blank" rel="noreferrer">
                                {source.title || source.domain || source.url}
                              </a>
                            ) : (
                              <span>{source.title || source.domain || source.type || 'Source'}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={closeEditor}
                      className="rounded-xl border border-slate-300 bg-slate-100 px-4 py-2 font-semibold text-slate-700 transition hover:bg-slate-200"
                    >
                      Close
                    </button>
                    <button
                      type="button"
                      onClick={saveVerified}
                      disabled={!isValidVerificationReason(form.verificationReason)}
                      className="inline-flex items-center gap-2 rounded-xl bg-teal-700 px-4 py-2 font-semibold text-white transition hover:bg-teal-600 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <CheckCircle className="h-4 w-4" />
                      Save Changes
                    </button>
                    <button
                      type="button"
                      onClick={deleteVerified}
                      disabled={loading}
                      className="inline-flex items-center gap-2 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-2 font-semibold text-red-700 transition hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Trash2 className="h-4 w-4" />
                      {confirmingVerifiedDeleteId === editingVerified.id ? 'Confirm Delete' : 'Delete Verified Record'}
                    </button>
                    {confirmingVerifiedDeleteId === editingVerified.id ? (
                      <button
                        type="button"
                        onClick={() => {
                          setConfirmingVerifiedDeleteId(null);
                          setMessage('');
                        }}
                        className="rounded-xl border border-slate-300 bg-slate-100 px-4 py-2 font-semibold text-slate-700 transition hover:bg-slate-200"
                      >
                        Cancel Delete
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {!settingsOpen ? (
          <CommandFeedback
            error={error}
            message={message}
            lastDeletedVerified={lastDeletedVerified}
            onUndoDelete={undoVerifiedDelete}
            loading={loading}
          />
        ) : null}
        {showBackToTop && !settingsOpen ? (
          <div className="pointer-events-none fixed inset-x-0 bottom-6 z-40 flex justify-center px-4">
            <button
              type="button"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="pointer-events-auto inline-flex items-center gap-2 rounded-full border border-teal-600/40 bg-white/95 px-4 py-3 text-sm font-bold text-teal-900 shadow-xl shadow-black/40 transition hover:border-teal-500/70 hover:bg-teal-700/10"
              aria-label="Back to top"
            >
              <ArrowUp className="h-4 w-4" aria-hidden="true" />
              Back to top
            </button>
          </div>
        ) : null}
        </div>
      </div>
      {settingsOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4 sm:p-8">
          <div className="w-full max-w-5xl rounded-3xl border border-slate-200 bg-white p-5 shadow-lg shadow-slate-900/10">
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-bold uppercase tracking-[0.18em] text-teal-700">Command Settings</p>
                <h2 className="mt-2 text-2xl font-bold text-slate-900">Health and session controls</h2>
                <p className="mt-1 text-sm leading-6 text-slate-400">
                  Settings live here so the main workspace stays focused on daily operations.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSettingsOpen(false)}
                className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-200 hover:text-slate-800"
                aria-label="Close settings"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <WorkCard title="Service Health" actionLabel="Run checks" onAction={runServiceChecks}>
                <HealthRow
                  label="Backend"
                  value={serviceHealth?.status === 'ok' ? 'ok' : serviceHealth?.status || 'unknown'}
                  detail={serviceHealth?.service || serviceHealth?.message || 'Checks /health/deep through the current app connection.'}
                />
                <HealthRow
                  label="Database"
                  value={serviceHealth?.checks?.db?.ok ? 'ok' : serviceHealth ? 'review' : 'unknown'}
                  detail={serviceHealth?.checks?.db?.productCache ? `Cache total: ${serviceHealth.checks.db.productCache.total}` : 'Product cache and local SQLite status.'}
                />
                <HealthRow
                  label="OpenAI key"
                  value={serviceHealth?.classifier?.openAIKey?.present ? 'ok' : serviceHealth ? 'review' : 'unknown'}
                  detail={serviceHealth?.classifier?.model ? `Model: ${serviceHealth.classifier.model}` : 'Presence only unless an intentional OpenAI check is run.'}
                />
                <HealthRow
                  label="Feature flags"
                  value={
                    serviceHealth?.featureFlags?.disabled?.length > 0
                      ? 'review'
                      : serviceHealth?.featureFlags
                        ? 'ok'
                        : 'unknown'
                  }
                  detail={
                    serviceHealth?.featureFlags
                      ? `${serviceHealth.featureFlags.disabled?.length || 0} disabled; ${serviceHealth.featureFlags.flags?.length || 0} tracked.`
                      : 'Controlled rollout switches for public analysis, lookup, image, and feedback features.'
                  }
                />
                <HealthRow
                  label="Cost controls"
                  value={
                    serviceHealth?.costControls?.emergencyPaused
                      ? 'review'
                      : serviceHealth?.costControls?.enabled
                        ? 'ok'
                        : serviceHealth
                          ? 'review'
                          : 'unknown'
                  }
                  detail={
                    serviceHealth?.costControls
                      ? `Analysis ${serviceHealth.costControls.features?.analysis?.remainingThisHour ?? 'unknown'} left this hour; image ${serviceHealth.costControls.features?.image?.remainingThisHour ?? 'unknown'} left.`
                      : 'App-side usage guardrails for expensive AI and image requests.'
                  }
                />
                <HealthRow
                  label="USDA"
                  value={serviceHealth?.integrations?.usda?.configured ? 'ok' : serviceHealth ? 'review' : 'unknown'}
                  detail={serviceHealth?.integrations?.usda?.enabled === false ? 'Disabled in backend config.' : 'Checks whether the USDA key is configured for product lookup fallback.'}
                />
                <HealthRow
                  label="Open Food Facts"
                  value={serviceHealth?.integrations?.openFoodFacts?.enabled === false ? 'review' : serviceHealth ? 'ok' : 'unknown'}
                  detail="Primary public product-source integration for barcode and product lookups."
                />
                <HealthRow
                  label="Tailscale"
                  value={window.location.hostname.endsWith('.ts.net') ? 'ok' : 'review'}
                  detail={window.location.hostname.endsWith('.ts.net') ? window.location.hostname : 'Not currently opened through the Tailscale URL.'}
                />
                <HealthRow
                  label="Frontend"
                  value="ok"
                  detail={`Command loaded from ${window.location.host || 'local browser'}.`}
                />
                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="font-semibold text-slate-900">Sentry event test</p>
                      <p className="mt-1 text-sm leading-6 text-slate-400">
                        Sends one frontend event and one backend event to confirm both projects are receiving data.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={sendSentryTestEvents}
                      disabled={loading}
                      className="inline-flex items-center justify-center gap-2 rounded-xl border border-teal-600/30 bg-teal-700/10 px-4 py-2 font-semibold text-teal-800 transition hover:bg-teal-700/20 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                      Send test
                    </button>
                  </div>
                </div>
              </WorkCard>

              <WorkCard title="Cost Activity" actionLabel="Refresh" onAction={runServiceChecks}>
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Allowed</p>
                    <p className="mt-2 text-2xl font-bold text-slate-900">{costActivity?.summary?.allowed24h ?? 0}</p>
                    <p className="mt-1 text-xs text-slate-500">Last 24 hours</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Blocked</p>
                    <p className="mt-2 text-2xl font-bold text-slate-900">{costActivity?.summary?.blocked24h ?? 0}</p>
                    <p className="mt-1 text-xs text-slate-500">Budget guardrails</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Window</p>
                    <p className="mt-2 text-2xl font-bold text-slate-900">{costActivity?.summary?.window || '24 hours'}</p>
                    <p className="mt-1 text-xs text-slate-500">Privacy-safe metadata only</p>
                  </div>
                </div>
                <div className="space-y-2">
                  {(costActivity?.events || []).slice(0, 6).map((event) => (
                    <div key={event.id} className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-3">
                      <div>
                        <p className="font-semibold text-slate-900">
                          {event.feature === 'image' ? 'Image request' : 'Analysis request'}
                        </p>
                        <p className="mt-1 text-xs text-slate-500">
                          {[formatEventTime(event.createdAt), event.source || event.mode, event.reason].filter(Boolean).join(' · ')}
                        </p>
                      </div>
                      <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${event.allowed ? 'bg-teal-600/10 text-teal-800' : 'bg-amber-400/15 text-amber-200'}`}>
                        {event.allowed ? 'allowed' : 'blocked'}
                      </span>
                    </div>
                  ))}
                  {(!costActivity?.events || costActivity.events.length === 0) && (
                    <p className="rounded-2xl border border-dashed border-slate-300 p-4 text-sm text-slate-500">
                      No cost activity logged yet.
                    </p>
                  )}
                </div>
              </WorkCard>

              <WorkCard title="Session and Naming">
                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <p className="font-semibold text-slate-900">Admin session</p>
                  <p className="mt-1 text-sm leading-6 text-slate-400">
                    The admin secret is kept only in app memory. Refreshing the browser, closing the tab, or logging out requires the secret again.
                  </p>
                  <button
                    type="button"
                    onClick={logout}
                    className="mt-4 inline-flex items-center gap-2 rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-2 font-semibold text-red-700 transition hover:bg-red-500/20"
                  >
                    <LogOut className="h-4 w-4" />
                    Log out
                  </button>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <p className="font-semibold text-slate-900">Naming</p>
                  <p className="mt-1 text-sm leading-6 text-slate-400">
                    Use Command for this admin operations workspace. Use Vision Board for the personal planning dashboard.
                  </p>
                </div>
              </WorkCard>
              <CommandFeedback
                error={error}
                message={message}
                lastDeletedVerified={lastDeletedVerified}
                onUndoDelete={undoVerifiedDelete}
                loading={loading}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
